import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Bot, Save, Stethoscope, FlaskConical, CheckCircle2, XCircle, Sparkles, ChevronDown, ChevronUp, Bell, Calendar, Link, Unlink } from 'lucide-react';
import api, { TIMEOUT_DIAGNOSTICO } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';

const C = {
  bg: '#f7f8fa',
  card: '#ffffff',
  border: '#d0d5dd',
  texto: '#101828',
  textoSecundario: '#475467',
  textoMudo: '#98a2b3',
  primaria: '#13305f',
  ok: '#16a34a',
  alerta: '#b91c1c'
};

function toggleArray(arr, valor) {
  return arr.includes(valor) ? arr.filter(v => v !== valor) : [...arr, valor];
}

const Q_PADRAO = {
  nome_secretaria: '', objetivo_conversa: '', publico_alvo: '', dados_coletar: [],
  descricao: '', servicos: '', horario: '',
  contato: [], pagamento: [], profissional: '',
  diferencial: '', restricoes: '', localizacao: '',
  tem_cursos: false, cursos_descricao: '',
  tratamento: 'voce'
};

export default function ConfiguracaoIA() {
  const { usuario } = useAuth();

  // Revelação de credencial já salva (superadmin). O valor NÃO entra no input:
  // fica num painel próprio, de leitura. Assim `agenda[campo]` continua com a
  // máscara e o PUT segue ignorando o campo — zero risco de o salvar reenviar o
  // segredo ou de a máscara sobrescrever o valor real.
  const [credenciaisReveladas, setCredenciaisReveladas] = useState({});
  const [revelandoCampo, setRevelandoCampo] = useState(null);

  async function revelarCredencial(campo) {
    setRevelandoCampo(campo);
    try {
      const { data } = await api.post('/admin/integracoes/revelar-credencial', { instancia, campo });
      setCredenciaisReveladas(r => ({ ...r, [campo]: data.valor }));
      if (data.aviso) toast(data.aviso);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Falha ao revelar a credencial');
    } finally {
      setRevelandoCampo(null);
    }
  }

  function ocultarCredencial(campo) {
    setCredenciaisReveladas(r => { const n = { ...r }; delete n[campo]; return n; });
  }

  async function copiarCredencial(campo) {
    try {
      await navigator.clipboard.writeText(credenciaisReveladas[campo] || '');
      toast.success(t('configuracao_ia.credencial_copiada'));
    } catch {
      toast.error(t('configuracao_ia.credencial_copiar_falhou'));
    }
  }

  // Renderiza o botão e o painel de revelação de um campo de segredo.
  // Só existe para superadmin — para os demais, devolve null e nada muda na tela.
  function renderRevelar(campo) {
    if (usuario?.role !== 'superadmin') return null;
    const revelado = credenciaisReveladas[campo];
    return (
      <div style={{ marginTop: -6, marginBottom: 12 }}>
        {revelado === undefined ? (
          <button onClick={() => revelarCredencial(campo)} disabled={revelandoCampo === campo}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: C.primaria, fontWeight: 600 }}>
            {revelandoCampo === campo ? t('configuracao_ia.credencial_revelando') : `👁 ${t('configuracao_ia.credencial_revelar')}`}
          </button>
        ) : (
          <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 10px' }}>
            <div style={{ fontSize: 11, color: '#78350f', marginBottom: 4 }}>
              {t('configuracao_ia.credencial_aviso')}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#78350f', wordBreak: 'break-all', marginBottom: 6 }}>
              {revelado || t('configuracao_ia.credencial_vazia')}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {revelado && (
                <button onClick={() => copiarCredencial(campo)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: '#78350f', fontWeight: 600 }}>
                  📋 {t('configuracao_ia.credencial_copiar')}
                </button>
              )}
              <button onClick={() => ocultarCredencial(campo)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: '#78350f', fontWeight: 600 }}>
                {t('configuracao_ia.credencial_ocultar')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
  const { t } = useT();
  const [instancias, setInstancias]           = useState([]);
  const [instancia, setInstancia]             = useState('');
  const [config, setConfig]                   = useState(null);
  const [quota, setQuota]                     = useState(null);
  const [carregando, setCarregando]           = useState(false);
  const [personaTipo, setPersonaTipo]         = useState('');
  const [estiloPersona, setEstiloPersona]     = useState('');
  const [questionario, setQuestionario]       = useState({ ...Q_PADRAO });
  const [gerandoConfig, setGerandoConfig]     = useState(false);
  const [modoAvancado, setModoAvancado]       = useState(false);
  const [salvando, setSalvando]               = useState(false);
  const [diagnostico, setDiagnostico]         = useState(null);
  const [diagLembretes, setDiagLembretes]               = useState(null);
  const [diagReativacao, setDiagReativacao]             = useState(null);
  const [diagPosAtend, setDiagPosAtend]                 = useState(null);
  const [carregandoDiagPosAtend, setCarregandoDiagPosAtend] = useState(false);
  const [diagEspera, setDiagEspera]                     = useState(null);
  const [carregandoDiagEspera, setCarregandoDiagEspera] = useState(false);
  const [carregandoDiagReativacao, setCarregandoDiagReativacao] = useState(false);
  const [carregandoDiagLembretes, setCarregandoDiagLembretes] = useState(false);
  const [perguntaTeste, setPerguntaTeste]     = useState('');
  const [respostaTeste, setRespostaTeste]     = useState('');
  const [testando, setTestando]               = useState(false);
  const [avisosConflito, setAvisosConflito]   = useState([]);
  const [agenda, setAgenda]                   = useState(null);
  const [salvandoAgenda, setSalvandoAgenda]   = useState(false);
  const [testandoAgenda, setTestandoAgenda]   = useState(false);
  const [avisoApiAgenda, setAvisoApiAgenda]   = useState(null);
  const [avecProfissionaisLista, setAvecProfissionaisLista] = useState([]);
  const [avecServicos, setAvecServicos]                    = useState([]);
  const [carregandoAvec, setCarregandoAvec]                = useState(false);
  const [sdProfissionais, setSdProfissionais]     = useState([]);
  const [carregandoSd, setCarregandoSd]           = useState(false);
  const [cpProfissionais, setCpProfissionais]     = useState([]);
  const [carregandoCp, setCarregandoCp]           = useState(false);
  const [cpClinicas, setCpClinicas]               = useState([]);
  const [boxCatalogo, setBoxCatalogo]             = useState(null);
  const [carregandoBox, setCarregandoBox]         = useState(false);
  const [boxDiagnostico, setBoxDiagnostico]       = useState(null);
  const [carregandoBoxDiag, setCarregandoBoxDiag] = useState(false);
  const [carregandoCpClinicas, setCarregandoCpClinicas] = useState(false);
  const [cpDiagnostico, setCpDiagnostico]         = useState(null);
  const [carregandoCpDiag, setCarregandoCpDiag]   = useState(false);
  const [cpAgTeste, setCpAgTeste]                 = useState(null);
  const [carregandoCpAgTeste, setCarregandoCpAgTeste] = useState(false);
  const [cpTelTeste, setCpTelTeste]               = useState('');
  const [outrasAberto, setOutrasAberto]               = useState(false);

  const TIPOS_NEGOCIO = [
    { id: 'saude',       emoji: '🏥', nome: t('configuracao_ia.tipo_saude_nome'),       desc: t('configuracao_ia.tipo_saude_desc') },
    { id: 'beleza',      emoji: '✂️', nome: t('configuracao_ia.tipo_beleza_nome'),      desc: t('configuracao_ia.tipo_beleza_desc') },
    { id: 'alimentacao', emoji: '🍽️', nome: t('configuracao_ia.tipo_alimentacao_nome'), desc: t('configuracao_ia.tipo_alimentacao_desc') },
    { id: 'educacao',    emoji: '📚', nome: t('configuracao_ia.tipo_educacao_nome'),    desc: t('configuracao_ia.tipo_educacao_desc') },
    { id: 'comercio',    emoji: '🛍️', nome: t('configuracao_ia.tipo_comercio_nome'),   desc: t('configuracao_ia.tipo_comercio_desc') },
    { id: 'servicos',    emoji: '⚙️', nome: t('configuracao_ia.tipo_servicos_nome'),   desc: t('configuracao_ia.tipo_servicos_desc') },
    { id: 'imoveis',     emoji: '🏠', nome: t('configuracao_ia.tipo_imoveis_nome'),    desc: t('configuracao_ia.tipo_imoveis_desc') },
    { id: 'juridico',    emoji: '⚖️', nome: t('configuracao_ia.tipo_juridico_nome'),   desc: t('configuracao_ia.tipo_juridico_desc') },
    { id: 'outro',       emoji: '💬', nome: t('configuracao_ia.tipo_outro_nome'),      desc: t('configuracao_ia.tipo_outro_desc') },
  ];

  const OPCOES_CONTATO = [
    t('configuracao_ia.opcao_contato_whatsapp'),
    t('configuracao_ia.opcao_contato_telefone'),
    t('configuracao_ia.opcao_contato_site'),
    t('configuracao_ia.opcao_contato_presencial'),
    t('configuracao_ia.opcao_contato_email'),
    t('configuracao_ia.opcao_contato_app'),
  ];

  const OPCOES_PAGAMENTO = [
    t('configuracao_ia.opcao_pag_dinheiro'),
    t('configuracao_ia.opcao_pag_credito'),
    t('configuracao_ia.opcao_pag_debito'),
    t('configuracao_ia.opcao_pag_pix'),
    t('configuracao_ia.opcao_pag_boleto'),
    t('configuracao_ia.opcao_pag_convenio'),
    t('configuracao_ia.opcao_pag_parcelamento'),
  ];

  // Vocabulário FECHADO, igual ao do backend: o prompt renderiza por BUSCA numa
  // tabela, então valor fora dela não vira instrução nenhuma.
  const TRATAMENTOS_OPCOES = [
    { id: 'voce',   nome: t('configuracao_ia.tratamento_voce_nome'),   desc: t('configuracao_ia.tratamento_voce_desc') },
    { id: 'senhor', nome: t('configuracao_ia.tratamento_senhor_nome'), desc: t('configuracao_ia.tratamento_senhor_desc') },
  ];

  const ESTILOS_PERSONA = [
    { id: 'humana',       emoji: '🤝', nome: t('configuracao_ia.estilo_humana_nome'),       desc: t('configuracao_ia.estilo_humana_desc') },
    { id: 'direta',       emoji: '⚡', nome: t('configuracao_ia.estilo_direta_nome'),       desc: t('configuracao_ia.estilo_direta_desc') },
    { id: 'persuasiva',   emoji: '🎯', nome: t('configuracao_ia.estilo_persuasiva_nome'),   desc: t('configuracao_ia.estilo_persuasiva_desc') },
    { id: 'profissional', emoji: '👔', nome: t('configuracao_ia.estilo_profissional_nome'), desc: t('configuracao_ia.estilo_profissional_desc') },
    { id: 'descontraida', emoji: '😊', nome: t('configuracao_ia.estilo_descontraida_nome'), desc: t('configuracao_ia.estilo_descontraida_desc') },
  ];

  const OBJETIVOS = [
    { id: 'agendar',    emoji: '📅', nome: t('configuracao_ia.obj_agendar_nome'),    desc: t('configuracao_ia.obj_agendar_desc') },
    { id: 'qualificar', emoji: '🎯', nome: t('configuracao_ia.obj_qualificar_nome'), desc: t('configuracao_ia.obj_qualificar_desc') },
    { id: 'vender',     emoji: '💰', nome: t('configuracao_ia.obj_vender_nome'),     desc: t('configuracao_ia.obj_vender_desc') },
    { id: 'informar',   emoji: '💬', nome: t('configuracao_ia.obj_informar_nome'),   desc: t('configuracao_ia.obj_informar_desc') },
  ];

  const DADOS_COLETAR = [
    t('configuracao_ia.dado_nome'),
    t('configuracao_ia.dado_telefone'),
    t('configuracao_ia.dado_email'),
    t('configuracao_ia.dado_cidade'),
    t('configuracao_ia.dado_servico'),
    t('configuracao_ia.dado_orcamento'),
    t('configuracao_ia.dado_data'),
    t('configuracao_ia.dado_empresa'),
    t('configuracao_ia.dado_quantidade'),
  ];

  useEffect(() => {
    api.get('/whatsapp/instancias').then(({ data }) => {
      const lista = (Array.isArray(data) ? data : [])
        .map(d => {
          if (typeof d === 'string') return d;
          return d?.instance?.instanceName || d?.instanceName || d?.name || d?.nome || d?.instancia || null;
        })
        .filter(Boolean);
      const unicas = Array.from(new Set(lista));
      setInstancias(unicas);
      if (unicas.length) setInstancia(unicas[0]);
    }).catch(() => {});
    api.get('/ia/quota').then(({ data }) => setQuota(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!instancia) return;
    setCarregando(true);
    setDiagnostico(null);
    setRespostaTeste('');
    api.get(`/ia/config/${encodeURIComponent(instancia)}`)
      .then(({ data }) => {
        setConfig(data);
        setPersonaTipo(data.persona_tipo || '');
        setQuestionario(data.questionario ? { ...Q_PADRAO, ...data.questionario } : { ...Q_PADRAO });
        setEstiloPersona(data.questionario?.estilo || '');
      })
      .catch(() => setConfig({ ativo: false, modelo: 'gpt-4o-mini', persona: '', base_conhecimento: '', fluxo_qualificacao: '', pausa_horas: 4, debounce_segundos: 20, instancia }))
      .finally(() => setCarregando(false));
    api.get(`/ia/config/${encodeURIComponent(instancia)}/agenda`)
      .then(({ data }) => setAgenda(data))
      .catch(() => setAgenda({ ativo: false, provedor: null, dias_semana: [1,2,3,4,5], hora_inicio: '08:00', hora_fim: '18:00', timezone: 'America/Sao_Paulo', duracao_minutos: 60, avec_api_key: '', avec_basehost: '', avec_email: '', avec_password: '', avec_salon_id: '', avec_slug: '', avec_profissional_id: '', avec_servico_id: '', avec_profissionais: [], simplesdental_sufixo: '', simplesdental_token: '', simplesdental_profissional_id: '', simplesdental_profissional_nome: '', simplesdental_profissionais: [] }));
  }, [instancia]); // eslint-disable-line

  function atualizarConfig(campo, valor) {
    setConfig(c => ({ ...c, [campo]: valor }));
  }

  function atualizarQ(campo, valor) {
    setQuestionario(q => ({ ...q, [campo]: valor }));
  }

  // Corpo do PUT /ia/config. O questionário vem do estado VIVO da tela, não de
  // `config.questionario` — os dois são estados separados, e o de `config` fica
  // defasado assim que o cliente edita um campo. Antes o questionário não era
  // enviado nem gravado por este endpoint: só o botão de gerar a concierge o
  // persistia, então editar horário, endereço ou serviços não fazia nada.
  function corpoConfig(extra = {}) {
    return {
      ...config,
      questionario: { ...questionario, estilo: estiloPersona },
      ...extra,
    };
  }

  async function toggleAtivo() {
    if (!config) return;
    const novoAtivo = !config.ativo;
    const novaConfig = { ...config, ativo: novoAtivo };
    setConfig(novaConfig);
    try {
      const { data } = await api.put(`/ia/config/${encodeURIComponent(instancia)}`, corpoConfig({ ativo: novoAtivo }));
      setConfig(data);
      toast.success(novoAtivo ? t('configuracao_ia.toast_ia_ativada') : t('configuracao_ia.toast_ia_desativada'));
    } catch (err) {
      setConfig(config);
      toast.error(err.response?.data?.erro || t('configuracao_ia.toast_erro_salvar'));
    }
  }

  async function gerarConfig() {
    if (!personaTipo) {
      toast.error(t('configuracao_ia.erro_tipo_negocio'));
      return;
    }
    if (!estiloPersona) {
      toast.error(t('configuracao_ia.erro_estilo'));
      return;
    }
    if (!questionario.objetivo_conversa) {
      toast.error(t('configuracao_ia.erro_objetivo'));
      return;
    }
    if (!questionario.descricao.trim()) {
      toast.error(t('configuracao_ia.erro_descricao'));
      return;
    }
    setGerandoConfig(true);
    try {
      const { data } = await api.post(`/ia/gerar-config/${encodeURIComponent(instancia)}`, {
        persona_tipo: personaTipo,
        estilo_persona: estiloPersona,
        questionario: { ...questionario, estilo: estiloPersona }
      });
      setConfig(data);
      toast.success(t('configuracao_ia.toast_configurada'));
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracao_ia.toast_erro_gerar'));
    } finally {
      setGerandoConfig(false);
    }
  }

  async function salvarNotificacao() {
    setSalvando(true);
    try {
      const { data } = await api.put(`/ia/config/${encodeURIComponent(instancia)}`, corpoConfig());
      setConfig(data);
      toast.success(t('configuracao_ia.toast_salvo'));
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracao_ia.toast_erro_salvar'));
    } finally {
      setSalvando(false);
    }
  }

  async function salvarAgenda() {
    setSalvandoAgenda(true);
    try {
      const { data } = await api.put(`/ia/config/${encodeURIComponent(instancia)}/agenda`, agenda);
      setAgenda(data);
      toast.success(t('configuracao_ia.agenda_toast_salvo'));
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracao_ia.toast_erro_salvar'));
    } finally {
      setSalvandoAgenda(false);
    }
  }

  // Desativa uma integração do card "Outras" e salva imediatamente.
  // updateAgenda() só atualiza o estado local; como setState é assíncrono,
  // não podemos chamar salvarAgenda() logo após — o estado ainda seria o antigo.
  // Esta função monta o payload correto diretamente e persiste.
  async function desativarIntegracao() {
    const ok = window.confirm(t('configuracao_ia.confirmar_desativar_integracao'));
    if (!ok) return;
    const agendaDesativada = { ...agenda, provedor: null };
    setAgenda(agendaDesativada);
    setSalvandoAgenda(true);
    try {
      const { data } = await api.put(`/ia/config/${encodeURIComponent(instancia)}/agenda`, agendaDesativada);
      setAgenda(data);
      toast.success(t('configuracao_ia.agenda_toast_salvo'));
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracao_ia.toast_erro_salvar'));
    } finally {
      setSalvandoAgenda(false);
    }
  }

  // Remove o provedor de calendário (botão "Nenhum") e persiste IMEDIATAMENTE.
  // Necessário porque o botão "Salvar" só é renderizado quando há um provedor
  // selecionado — ao zerar o provedor, o usuário perderia a forma de salvar a
  // remoção. Mesmo padrão de desativarIntegracao, sem o confirm (troca de provedor
  // de calendário é reversível e de baixo impacto).
  async function removerProvedorCalendario() {
    const agendaSemProvedor = { ...agenda, provedor: null };
    setAgenda(agendaSemProvedor);
    setSalvandoAgenda(true);
    try {
      const { data } = await api.put(`/ia/config/${encodeURIComponent(instancia)}/agenda`, agendaSemProvedor);
      setAgenda(data);
      toast.success(t('configuracao_ia.agenda_toast_salvo'));
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracao_ia.toast_erro_salvar'));
    } finally {
      setSalvandoAgenda(false);
    }
  }

  async function testarAgenda() {
    setTestandoAgenda(true);
    try {
      const { data } = await api.post(`/ia/config/${encodeURIComponent(instancia)}/agenda/testar`);
      if (data.ok) {
        // URL do iCloud descoberta automaticamente — reflete no formulário
        if (data.calendar_url) setAgenda(a => ({ ...a, icloud_calendar_url: data.calendar_url }));
        // Sucesso PARCIAL: no Simples Dental o agendamento usa o link público e
        // funciona mesmo com token que a API autenticada recusa — e aí só os
        // lembretes morrem. Um "conexão ok" sozinho esconderia isso.
        if (data.avisoApi) {
          toast(data.avisoApi, { icon: '⚠️', duration: 12000 });
          setAvisoApiAgenda(data.avisoApi);
        } else {
          setAvisoApiAgenda(null);
          toast.success(t('configuracao_ia.agenda_toast_conexao_ok'));
        }
      } else {
        toast.error(data.erro || t('configuracao_ia.agenda_toast_conexao_erro'));
      }
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracao_ia.agenda_toast_conexao_erro'));
    } finally {
      setTestandoAgenda(false);
    }
  }

  async function autorizarGoogle() {
    try {
      const { data } = await api.get(`/ia/config/${encodeURIComponent(instancia)}/agenda/google-auth-url`);
      const popup = window.open(data.url, 'googleAuth', 'width=520,height=620,scrollbars=yes');
      const poll = setInterval(async () => {
        if (!popup || popup.closed) {
          clearInterval(poll);
          const { data: ag } = await api.get(`/ia/config/${encodeURIComponent(instancia)}/agenda`);
          setAgenda(ag);
          if (ag.google_conectado) toast.success(t('configuracao_ia.agenda_google_conectado'));
        }
      }, 1500);
      window.addEventListener('message', (e) => {
        if (e.data?.googleCalendarConectado) {
          clearInterval(poll);
          popup?.close();
          api.get(`/ia/config/${encodeURIComponent(instancia)}/agenda`)
            .then(({ data: ag }) => { setAgenda(ag); toast.success(t('configuracao_ia.agenda_google_conectado')); });
        }
      }, { once: true });
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracao_ia.agenda_google_erro_auth'));
    }
  }

  async function desconectarGoogle() {
    try {
      await api.post(`/ia/config/${encodeURIComponent(instancia)}/agenda/google-desconectar`);
      setAgenda(a => ({ ...a, google_conectado: false }));
      toast.success(t('configuracao_ia.agenda_google_desconectado'));
    } catch (err) {
      toast.error(err.response?.data?.erro || t('comum.erro_generico'));
    }
  }

  async function carregarAvecDados() {
    const apiKey = agenda?.avec_api_key?.trim();
    if (!apiKey || apiKey === '••••••••') {
      toast.error('Preencha a API Key antes de buscar os profissionais');
      return;
    }
    setCarregandoAvec(true);
    try {
      // Envia a API Key digitada (mesmo antes de salvar) — o backend usa o param
      // com fallback para a config salva. Host fixo (api.avec.beauty) no backend.
      const params = { api_key: apiKey };
      const [{ data: profs }, { data: servs }] = await Promise.all([
        api.get(`/ia/config/${encodeURIComponent(instancia)}/agenda/avec/profissionais`, { params }),
        api.get(`/ia/config/${encodeURIComponent(instancia)}/agenda/avec/servicos`, { params })
      ]);
      setAvecProfissionaisLista(Array.isArray(profs) ? profs : []);
      setAvecServicos(Array.isArray(servs) ? servs : []);
      if (!(profs?.length)) toast.error('Nenhum profissional retornado — verifique a API Key');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao carregar dados Avec');
    } finally {
      setCarregandoAvec(false);
    }
  }

  function addProfissionalAvec(id, nome) {
    setAgenda(a => {
      const profs = Array.isArray(a.avec_profissionais) ? a.avec_profissionais : [];
      if (profs.some(p => String(p.id) === String(id))) return a;
      return { ...a, avec_profissionais: [...profs, { id: String(id), nome, especialidade: '' }] };
    });
  }

  function removeProfissionalAvec(id) {
    setAgenda(a => ({
      ...a,
      avec_profissionais: (a.avec_profissionais || []).filter(p => String(p.id) !== String(id))
    }));
  }

  function updateEspecialidadeAvec(id, esp) {
    setAgenda(a => ({
      ...a,
      avec_profissionais: (a.avec_profissionais || []).map(p =>
        String(p.id) === String(id) ? { ...p, especialidade: esp } : p
      )
    }));
  }

  async function buscarProfissionaisSD() {
    const sufixo = agenda?.simplesdental_sufixo?.trim();
    const token  = agenda?.simplesdental_token?.trim();
    if (!sufixo || !token || token === '••••••••') {
      toast.error('Preencha o sufixo do link e o token antes de buscar');
      return;
    }
    setCarregandoSd(true);
    try {
      const { data } = await api.get(
        `/ia/config/${encodeURIComponent(instancia)}/agenda/simplesdental/profissionais`,
        { params: { sufixo, token } }
      );
      setSdProfissionais(Array.isArray(data) ? data : []);
      if (!data.length) toast.error('Nenhum profissional encontrado para este link');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao buscar profissionais');
    } finally {
      setCarregandoSd(false);
    }
  }

  function addProfissionalSD(id, nome) {
    setAgenda(a => {
      const profs = Array.isArray(a.simplesdental_profissionais) ? a.simplesdental_profissionais : [];
      if (profs.some(p => String(p.id) === String(id))) return a;
      return { ...a, simplesdental_profissionais: [...profs, { id: String(id), nome, especialidade: '' }] };
    });
  }

  function removeProfissionalSD(id) {
    setAgenda(a => ({
      ...a,
      simplesdental_profissionais: (a.simplesdental_profissionais || []).filter(p => String(p.id) !== String(id))
    }));
  }

  function updateEspecialidadeSD(id, especialidade) {
    setAgenda(a => ({
      ...a,
      simplesdental_profissionais: (a.simplesdental_profissionais || []).map(p =>
        String(p.id) === String(id) ? { ...p, especialidade } : p
      )
    }));
  }

  // ── Clinicorp ──────────────────────────────────
  // A API do Clinicorp autentica por HTTP Basic. Mandamos usuário e senha na query
  // para funcionar DURANTE o setup, antes de salvar; quando a senha está mascarada
  // (já salva), o backend usa a que está no banco.
  function credenciaisCP() {
    return {
      usuario:       agenda?.clinicorp_usuario || '',
      senha:         agenda?.clinicorp_token || '',
      subscriber_id: agenda?.clinicorp_subscriber_id || '',
      clinic_id:     agenda?.clinicorp_clinic_id || ''
    };
  }

  async function buscarProfissionaisCP() {
    if (!agenda?.clinicorp_usuario || !agenda?.clinicorp_token) {
      toast.error('Informe usuário e senha da API antes de buscar');
      return;
    }
    setCarregandoCp(true);
    try {
      const { data } = await api.get(
        `/ia/config/${encodeURIComponent(instancia)}/agenda/clinicorp/profissionais`,
        { params: credenciaisCP() }
      );
      setCpProfissionais(Array.isArray(data) ? data : []);
      if (!data.length) toast('Nenhum profissional encontrado');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Falha ao buscar profissionais');
    } finally {
      setCarregandoCp(false);
    }
  }

  // Roda a bateria de verificações somente-leitura e mostra o resultado na tela.
  // Substitui o script de terminal: o cliente nunca precisa sair do painel.
  // Erro de 🩺: corte de TEMPO do navegador NÃO é falha da integração, e dizer
  // "Falha ao rodar o diagnóstico" para um timeout manda o cliente procurar defeito
  // onde não há — foi o que aconteceu com o Box em 24/10. Requisição abortada do
  // lado do cliente chega SEM `response`, então é por isso que se distingue aqui.
  function erroDiagnostico(err, generico) {
    if (err?.response?.data?.erro) return err.response.data.erro;
    if (err?.code === 'ECONNABORTED') return t('configuracao_ia.diag_timeout');
    if (!err?.response)              return t('configuracao_ia.diag_rede');
    return generico;
  }

  async function rodarDiagnosticoCP() {
    setCarregandoCpDiag(true);
    setCpDiagnostico(null);
    try {
      const { data } = await api.post(
        `/ia/config/${encodeURIComponent(instancia)}/agenda/clinicorp/diagnostico`,
        {}, { params: credenciaisCP(), timeout: TIMEOUT_DIAGNOSTICO }
      );
      setCpDiagnostico(data);
    } catch (err) {
      toast.error(erroDiagnostico(err, 'Falha ao rodar o diagnóstico'));
    } finally {
      setCarregandoCpDiag(false);
    }
  }

  // Somente leitura: pergunta ao backend por qual portão do cron de lembretes esta
  // instância está passando (ou parando) e o que sairia neste ciclo. Vale para
  // qualquer provedor — o motor de antecedências é comum a todos.
  async function rodarDiagnosticoLembretes() {
    setCarregandoDiagLembretes(true);
    setDiagLembretes(null);
    try {
      const { data } = await api.post(
        `/ia/config/${encodeURIComponent(instancia)}/agenda/lembretes/diagnostico`, {},
        { timeout: TIMEOUT_DIAGNOSTICO }
      );
      setDiagLembretes(data);
    } catch (err) {
      toast.error(erroDiagnostico(err, t('configuracao_ia.lembretes_diag_erro')));
    } finally {
      setCarregandoDiagLembretes(false);
    }
  }

  // Somente leitura: mesmos portões do cron de reativação, na mesma ordem, mais a
  // prévia de quem sairia e o motivo de quem for pulado.
  async function rodarDiagnosticoReativacao() {
    setCarregandoDiagReativacao(true);
    setDiagReativacao(null);
    try {
      const { data } = await api.post(
        `/ia/config/${encodeURIComponent(instancia)}/agenda/reativacao/diagnostico`, {},
        { timeout: TIMEOUT_DIAGNOSTICO }
      );
      setDiagReativacao(data);
    } catch (err) {
      toast.error(erroDiagnostico(err, t('configuracao_ia.reativacao_diag_erro')));
    } finally {
      setCarregandoDiagReativacao(false);
    }
  }

  // Mesmo recurso e mesmo motivo do 🩺 da reativação: este motor também é opt-in
  // com portões silenciosos, e as duas perguntas que o desenho dele cria ("ainda
  // não deu o tempo", "visita velha demais") só se respondem vendo os passos.
  async function rodarDiagnosticoPosAtend() {
    setCarregandoDiagPosAtend(true);
    setDiagPosAtend(null);
    try {
      const { data } = await api.post(
        `/ia/config/${encodeURIComponent(instancia)}/agenda/pos-atendimento/diagnostico`, {},
        { timeout: TIMEOUT_DIAGNOSTICO }
      );
      setDiagPosAtend(data);
    } catch (err) {
      toast.error(erroDiagnostico(err, t('configuracao_ia.pos_atend_diag_erro')));
    } finally {
      setCarregandoDiagPosAtend(false);
    }
  }

  // A fila de espera é invisível por natureza: sem este 🩺 o cliente não tem como
  // saber quem está esperando o quê, nem por que alguém não foi avisado.
  async function rodarDiagnosticoEspera() {
    setCarregandoDiagEspera(true);
    setDiagEspera(null);
    try {
      const { data } = await api.post(
        `/ia/config/${encodeURIComponent(instancia)}/agenda/espera/diagnostico`, {},
        { timeout: TIMEOUT_DIAGNOSTICO }
      );
      setDiagEspera(data);
    } catch (err) {
      toast.error(erroDiagnostico(err, t('configuracao_ia.espera_diag_erro')));
    } finally {
      setCarregandoDiagEspera(false);
    }
  }

  // ⚠️ Cria consulta REAL na agenda da clínica — daí a confirmação explícita.
  async function rodarAgendamentoTesteCP() {    const tel = (cpTelTeste || '').replace(/\D/g, '');
    if (tel.length < 10) {
      toast.error('Informe um telefone com DDD para o paciente de teste');
      return;
    }
    const ok = window.confirm(
      'Isto vai criar uma CONSULTA DE VERDADE na agenda do Clinicorp, no primeiro horário livre.\n\n' +
      'Serve para conferir se o dia e a hora chegam corretos. Você poderá cancelar logo em seguida, ' +
      'pelo botão que aparece no resultado.\n\nPode continuar?'
    );
    if (!ok) return;

    setCarregandoCpAgTeste(true);
    setCpAgTeste(null);
    try {
      const { data } = await api.post(
        `/ia/config/${encodeURIComponent(instancia)}/agenda/clinicorp/agendamento-teste`,
        { telefone: tel }, { params: credenciaisCP() }
      );
      setCpAgTeste(data);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Falha no agendamento de teste');
    } finally {
      setCarregandoCpAgTeste(false);
    }
  }

  async function cancelarAgendamentoTesteCP() {
    if (!cpAgTeste?.consulta_id) return;
    try {
      await api.post(
        `/ia/config/${encodeURIComponent(instancia)}/agenda/clinicorp/agendamento-teste/cancelar`,
        { consulta_id: cpAgTeste.consulta_id }, { params: credenciaisCP() }
      );
      toast.success('Agendamento de teste cancelado');
      setCpAgTeste(null);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Falha ao cancelar');
    }
  }

  // A URL é por barbearia e o token é o AGENT_API_TOKEN dela. Mandamos na query
  // para funcionar durante o setup, antes de salvar.
  function credenciaisBox() {
    return {
      url:     agenda?.boxbarbearia_url || '',
      token:   agenda?.boxbarbearia_token || '',
      servico: agenda?.boxbarbearia_servico_padrao || ''
    };
  }

  async function buscarCatalogoBox() {
    if (!agenda?.boxbarbearia_url || !agenda?.boxbarbearia_token) {
      toast.error('Informe a URL da API e o token antes de buscar');
      return;
    }
    setCarregandoBox(true);
    try {
      const { data } = await api.get(
        `/ia/config/${encodeURIComponent(instancia)}/agenda/boxbarbearia/catalogo`,
        { params: credenciaisBox() }
      );
      setBoxCatalogo(data);
      if (!data?.servicos?.length) toast('Nenhum serviço cadastrado na barbearia');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Falha ao buscar o catálogo');
    } finally {
      setCarregandoBox(false);
    }
  }

  async function rodarDiagnosticoBox() {
    setCarregandoBoxDiag(true);
    setBoxDiagnostico(null);
    try {
      const { data } = await api.post(
        `/ia/config/${encodeURIComponent(instancia)}/agenda/boxbarbearia/diagnostico`,
        {}, { params: credenciaisBox(), timeout: TIMEOUT_DIAGNOSTICO }
      );
      setBoxDiagnostico(data);
    } catch (err) {
      toast.error(erroDiagnostico(err, 'Falha ao rodar o diagnóstico'));
    } finally {
      setCarregandoBoxDiag(false);
    }
  }

  async function buscarClinicasCP() {
    if (!agenda?.clinicorp_usuario || !agenda?.clinicorp_token) {
      toast.error('Informe usuário e senha da API antes de buscar');
      return;
    }
    setCarregandoCpClinicas(true);
    try {
      const { data } = await api.get(
        `/ia/config/${encodeURIComponent(instancia)}/agenda/clinicorp/clinicas`,
        { params: credenciaisCP() }
      );
      const lista = Array.isArray(data) ? data : [];
      setCpClinicas(lista);
      if (!lista.length) toast('Nenhuma clínica encontrada');
      // Conta com uma clínica só: seleciona sozinho, em vez de obrigar o cliente
      // a escolher entre uma opção única.
      // Usa setAgenda direto: o helper updateAgenda é declarado dentro do bloco de
      // render da agenda e não existe aqui, no corpo do componente.
      else if (lista.length === 1 && !agenda.clinicorp_clinic_id) {
        setAgenda(a => ({ ...a, clinicorp_clinic_id: String(lista[0].id) }));
      }
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Falha ao buscar clínicas');
    } finally {
      setCarregandoCpClinicas(false);
    }
  }

  function addProfissionalCP(id, nome) {
    setAgenda(a => {
      const profs = Array.isArray(a.clinicorp_profissionais) ? a.clinicorp_profissionais : [];
      if (profs.some(p => String(p.id) === String(id))) return a;
      return { ...a, clinicorp_profissionais: [...profs, { id: String(id), nome, especialidade: '' }] };
    });
  }

  function removeProfissionalCP(id) {
    setAgenda(a => ({
      ...a,
      clinicorp_profissionais: (a.clinicorp_profissionais || []).filter(p => String(p.id) !== String(id))
    }));
  }

  function updateEspecialidadeCP(id, especialidade) {
    setAgenda(a => ({
      ...a,
      clinicorp_profissionais: (a.clinicorp_profissionais || []).map(p =>
        String(p.id) === String(id) ? { ...p, especialidade } : p
      )
    }));
  }

  async function salvarAvancado(confirmarConflitos = false) {
    setSalvando(true);
    try {
      const { data } = await api.put(`/ia/config/${encodeURIComponent(instancia)}`, corpoConfig({
        validar_conflitos: true,
        confirmar_conflitos: confirmarConflitos
      }));
      const { avisos = [], ...configSalva } = data;
      setConfig(configSalva);
      setAvisosConflito(avisos);
      toast.success(t('configuracao_ia.toast_salvo'));
    } catch (err) {
      const resp = err.response?.data;
      // 409 → conflito detectado: mostra avisos e pergunta se deseja prosseguir
      if (err.response?.status === 409 && resp?.requer_confirmacao) {
        setAvisosConflito(resp.avisos || []);
        const lista = (resp.avisos || []).map(a => `• ${a}`).join('\n');
        const ok = window.confirm(
          `${t('configuracao_ia.conflito_confirm')}\n\n${lista}\n\n${t('configuracao_ia.conflito_confirm_pergunta')}`
        );
        if (ok) {
          setSalvando(false);
          return salvarAvancado(true);
        }
      } else {
        toast.error(resp?.erro || t('configuracao_ia.toast_erro_salvar'));
      }
    } finally {
      setSalvando(false);
    }
  }

  async function rodarDiagnostico() {
    try {
      const { data } = await api.get(`/ia/diagnostico/${encodeURIComponent(instancia)}`);
      setDiagnostico(data);
    } catch (err) {
      toast.error(err.response?.data?.erro || t('configuracao_ia.toast_erro_diagnostico'));
    }
  }

  async function testar() {
    if (!perguntaTeste.trim()) return;
    setTestando(true);
    setRespostaTeste('');
    try {
      const { data } = await api.post(`/ia/teste/${encodeURIComponent(instancia)}`, { pergunta: perguntaTeste });
      setRespostaTeste(data.resposta || t('configuracao_ia.resposta_vazia'));
    } catch (err) {
      setRespostaTeste('');
      toast.error(err.response?.data?.erro || t('configuracao_ia.toast_erro_testar'));
    } finally {
      setTestando(false);
    }
  }

  const inputBase = {
    width: '100%', padding: '8px 12px',
    background: C.card, color: C.texto,
    border: `1px solid ${C.border}`, borderRadius: 6,
    fontSize: 14, outline: 'none', boxSizing: 'border-box'
  };

  const labelBase = {
    display: 'block', marginBottom: 6,
    color: C.textoSecundario, fontSize: 13, fontWeight: 600
  };

  const card = {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: 16
  };

  const jaConfigurada = !!(config?.persona);

  if (!usuario) return <Navigate to="/" replace />;

  return (
    <div className="page" style={{ color: C.texto }}>
      <div style={{ marginBottom: 16 }}>
        <h1 className="page-h" style={{ marginBottom: 4, color: C.texto }}>
          <Bot size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          {t('configuracao_ia.titulo')}
        </h1>
        <p className="page-h-sub" style={{ color: C.textoSecundario }}>
          {t('configuracao_ia.subtitulo')}
        </p>
      </div>

      {quota && !quota.ilimitado && quota.limite === 0 && (
        <div style={{ ...card, marginBottom: 16, padding: 14, background: '#fffbeb', border: '1px solid #f59e0b' }}>
          <strong style={{ color: '#92400e', display: 'block', marginBottom: 4 }}>
            ⚠️ {t('configuracao_ia.quota_sem_plano', { plano: quota.plano || 'Starter' })}
          </strong>
          <span style={{ color: '#78350f', fontSize: 13 }}>
            {t('configuracao_ia.quota_upgrade')}
          </span>
        </div>
      )}

      {quota && (quota.ilimitado || quota.limite > 0) && (
        <div style={{ ...card, marginBottom: 16, padding: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ color: C.texto }}>{t('configuracao_ia.uso_mes', { mes: quota.mes })}</strong>
            <span style={{ color: C.textoSecundario, fontSize: 14 }}>
              {t('configuracao_ia.uso_mensagens', { usado: quota.usado, limite: quota.ilimitado ? '∞' : quota.limite })}
            </span>
          </div>
          {!quota.ilimitado && (
            <div style={{ height: 6, background: '#e4e7ec', borderRadius: 3, marginTop: 10, overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min(100, (quota.usado / quota.limite) * 100)}%`,
                height: '100%',
                background: quota.usado >= quota.limite ? C.alerta : C.ok
              }} />
            </div>
          )}
        </div>
      )}

      <div style={card}>
        {instancias.length === 0 ? (
          <p style={{ color: C.textoSecundario }}>
            {t('configuracao_ia.sem_instancia')}
          </p>
        ) : (
          <>
            <label style={labelBase}>{t('configuracao_ia.label_instancia')}</label>
            <select
              value={instancia}
              onChange={(e) => setInstancia(e.target.value)}
              style={{ ...inputBase, marginBottom: 18 }}
            >
              {instancias.map(i => <option key={i} value={i}>{i}</option>)}
            </select>

            {!carregando && config && (
              <>
                <div
                  onClick={quota && !quota.ilimitado && quota.limite === 0 ? undefined : toggleAtivo}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 16px', borderRadius: 6, marginBottom: 24,
                    cursor: quota && !quota.ilimitado && quota.limite === 0 ? 'not-allowed' : 'pointer',
                    opacity: quota && !quota.ilimitado && quota.limite === 0 ? 0.5 : 1,
                    background: C.card, border: `1px solid ${config.ativo ? C.ok : C.border}`
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: C.texto, fontSize: 15 }}>{t('configuracao_ia.toggle_titulo')}</div>
                    <div style={{ fontSize: 12, color: config.ativo ? C.ok : C.textoSecundario, marginTop: 4, fontWeight: 500 }}>
                      {config.ativo ? t('configuracao_ia.toggle_ativo') : t('configuracao_ia.toggle_inativo')}
                    </div>
                  </div>
                  <div style={{ width: 44, height: 24, borderRadius: 12, position: 'relative', flexShrink: 0, background: config.ativo ? C.ok : '#cbd5e1', transition: 'background 0.2s' }}>
                    <div style={{ position: 'absolute', top: 3, left: config.ativo ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: C.texto, marginBottom: 4, marginTop: 0 }}>
                    {t('configuracao_ia.sec_secretaria')}
                  </h2>
                  <p style={{ fontSize: 13, color: C.textoSecundario, marginBottom: 16, marginTop: 0 }}>
                    {t('configuracao_ia.sec_secretaria_desc')}
                  </p>

                  {jaConfigurada && (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: '#f0fdf4', border: '1px solid #bbf7d0',
                      borderRadius: 6, padding: '6px 12px', marginBottom: 16,
                      fontSize: 13, color: C.ok, fontWeight: 600
                    }}>
                      <CheckCircle2 size={14} />
                      {t('configuracao_ia.configurada')}
                    </div>
                  )}

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ ...labelBase, marginBottom: 10, fontSize: 14 }}>
                      {t('configuracao_ia.step1')}
                    </label>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 8
                    }}>
                      {TIPOS_NEGOCIO.map(tp => {
                        const selecionado = personaTipo === tp.id;
                        return (
                          <div
                            key={tp.id}
                            onClick={() => setPersonaTipo(tp.id)}
                            style={{
                              padding: '10px 8px', borderRadius: 8, cursor: 'pointer',
                              border: `2px solid ${selecionado ? C.primaria : C.border}`,
                              background: selecionado ? '#eff6ff' : C.card,
                              textAlign: 'center',
                              transition: 'border-color 0.15s, background 0.15s'
                            }}
                          >
                            <div style={{ fontSize: 22, lineHeight: 1, marginBottom: 4 }}>{tp.emoji}</div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: selecionado ? C.primaria : C.texto, lineHeight: 1.3 }}>{tp.nome}</div>
                            <div style={{ fontSize: 11, color: C.textoMudo, marginTop: 2, lineHeight: 1.3 }}>{tp.desc}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ ...labelBase, marginBottom: 10, fontSize: 14 }}>
                      {t('configuracao_ia.step2')}
                    </label>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                      gap: 8
                    }}>
                      {ESTILOS_PERSONA.map(e => {
                        const selecionado = estiloPersona === e.id;
                        return (
                          <div
                            key={e.id}
                            onClick={() => setEstiloPersona(e.id)}
                            style={{
                              padding: '10px 8px', borderRadius: 8, cursor: 'pointer',
                              border: `2px solid ${selecionado ? C.primaria : C.border}`,
                              background: selecionado ? '#eff6ff' : C.card,
                              textAlign: 'center',
                              transition: 'border-color 0.15s, background 0.15s'
                            }}
                          >
                            <div style={{ fontSize: 22, lineHeight: 1, marginBottom: 4 }}>{e.emoji}</div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: selecionado ? C.primaria : C.texto, lineHeight: 1.3 }}>{e.nome}</div>
                            <div style={{ fontSize: 11, color: C.textoMudo, marginTop: 2, lineHeight: 1.3 }}>{e.desc}</div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Forma de tratamento — vira regra determinística no prompt (C2) */}
                    <div style={{ marginTop: 14 }}>
                      <label style={{ ...labelBase, fontSize: 13 }}>{t('configuracao_ia.label_tratamento')}</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {TRATAMENTOS_OPCOES.map(op => {
                          const sel = (questionario.tratamento || 'voce') === op.id;
                          return (
                            <div
                              key={op.id}
                              onClick={() => atualizarQ('tratamento', op.id)}
                              style={{
                                flex: 1, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                                border: `2px solid ${sel ? C.primaria : C.border}`,
                                background: sel ? '#eff6ff' : C.card, textAlign: 'center'
                              }}
                            >
                              <div style={{ fontSize: 12, fontWeight: 700, color: sel ? C.primaria : C.texto }}>{op.nome}</div>
                              <div style={{ fontSize: 11, color: C.textoMudo, marginTop: 2 }}>{op.desc}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ ...labelBase, marginBottom: 12, fontSize: 14 }}>
                      {t('configuracao_ia.step3')}
                    </label>

                    {/* Nome da secretária */}
                    <div style={{ marginBottom: 14 }}>
                      <label style={labelBase}>{t('configuracao_ia.label_nome_secretaria')}</label>
                      <input
                        type="text"
                        value={questionario.nome_secretaria}
                        onChange={e => atualizarQ('nome_secretaria', e.target.value)}
                        placeholder={t('configuracao_ia.placeholder_nome_secretaria')}
                        style={{ ...inputBase, maxWidth: 260 }}
                      />
                      <p style={{ fontSize: 12, color: C.textoMudo, marginTop: 4, marginBottom: 0 }}>
                        {t('configuracao_ia.hint_nome_secretaria')}
                      </p>
                    </div>

                    {/* Objetivo principal */}
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ ...labelBase, marginBottom: 10 }}>{t('configuracao_ia.label_objetivo_conversa')}</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                        {OBJETIVOS.map(obj => {
                          const sel = questionario.objetivo_conversa === obj.id;
                          return (
                            <div
                              key={obj.id}
                              onClick={() => atualizarQ('objetivo_conversa', obj.id)}
                              style={{
                                padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                                border: `2px solid ${sel ? C.primaria : C.border}`,
                                background: sel ? '#eff6ff' : C.card,
                                display: 'flex', alignItems: 'flex-start', gap: 10,
                                transition: 'border-color 0.15s, background 0.15s'
                              }}
                            >
                              <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{obj.emoji}</span>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: sel ? C.primaria : C.texto, lineHeight: 1.3 }}>{obj.nome}</div>
                                <div style={{ fontSize: 11, color: C.textoMudo, marginTop: 2, lineHeight: 1.4 }}>{obj.desc}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Descrição do negócio */}
                    <div style={{ marginBottom: 14 }}>
                      <label style={labelBase}>{t('configuracao_ia.label_descricao_negocio')}</label>
                      <textarea
                        rows={2}
                        value={questionario.descricao}
                        onChange={e => atualizarQ('descricao', e.target.value)}
                        placeholder={t('configuracao_ia.placeholder_descricao_negocio')}
                        style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>

                    {/* Público-alvo */}
                    <div style={{ marginBottom: 14 }}>
                      <label style={labelBase}>{t('configuracao_ia.label_publico_alvo')}</label>
                      <textarea
                        rows={2}
                        value={questionario.publico_alvo}
                        onChange={e => atualizarQ('publico_alvo', e.target.value)}
                        placeholder={t('configuracao_ia.placeholder_publico_alvo')}
                        style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <label style={labelBase}>{t('configuracao_ia.label_servicos')}</label>
                      <textarea
                        rows={3}
                        value={questionario.servicos}
                        onChange={e => atualizarQ('servicos', e.target.value)}
                        placeholder={t('configuracao_ia.placeholder_servicos')}
                        style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>

                    {/* Dados a coletar */}
                    <div style={{ marginBottom: 14 }}>
                      <label style={labelBase}>{t('configuracao_ia.label_dados_coletar')}</label>
                      <p style={{ fontSize: 12, color: C.textoMudo, marginTop: 0, marginBottom: 8 }}>
                        {t('configuracao_ia.hint_dados_coletar')}
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6 }}>
                        {DADOS_COLETAR.map(op => {
                          const sel = (questionario.dados_coletar || []).includes(op);
                          return (
                            <label key={op} style={{
                              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                              padding: '6px 10px', borderRadius: 6, fontSize: 13,
                              border: `1px solid ${sel ? C.primaria : C.border}`,
                              background: sel ? '#eff6ff' : C.card,
                              color: sel ? C.primaria : C.texto, fontWeight: sel ? 600 : 400,
                              transition: 'all 0.15s'
                            }}>
                              <input
                                type="checkbox"
                                checked={sel}
                                onChange={() => atualizarQ('dados_coletar', toggleArray(questionario.dados_coletar || [], op))}
                                style={{ display: 'none' }}
                              />
                              <span style={{ fontSize: 14 }}>{sel ? '✓' : '+'}</span>
                              {op}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <label style={labelBase}>{t('configuracao_ia.label_horario')}</label>
                      <input
                        type="text"
                        value={questionario.horario}
                        onChange={e => atualizarQ('horario', e.target.value)}
                        placeholder={t('configuracao_ia.placeholder_horario')}
                        style={inputBase}
                      />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <label style={labelBase}>{t('configuracao_ia.label_contato')}</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {OPCOES_CONTATO.map(op => {
                          const sel = (questionario.contato || []).includes(op);
                          return (
                            <label key={op} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13 }}>
                              <input
                                type="checkbox"
                                checked={sel}
                                onChange={() => atualizarQ('contato', toggleArray(questionario.contato || [], op))}
                              />
                              {op}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <label style={labelBase}>{t('configuracao_ia.label_pagamento')}</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {OPCOES_PAGAMENTO.map(op => {
                          const sel = (questionario.pagamento || []).includes(op);
                          return (
                            <label key={op} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13 }}>
                              <input
                                type="checkbox"
                                checked={sel}
                                onChange={() => atualizarQ('pagamento', toggleArray(questionario.pagamento || [], op))}
                              />
                              {op}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <label style={labelBase}>{t('configuracao_ia.label_profissional')} <span style={{ color: C.textoMudo, fontWeight: 400 }}>({t('configuracao_ia.opcional')})</span></label>
                      <textarea
                        rows={2}
                        value={questionario.profissional}
                        onChange={e => atualizarQ('profissional', e.target.value)}
                        placeholder={t('configuracao_ia.placeholder_profissional')}
                        style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <label style={labelBase}>{t('configuracao_ia.label_diferencial')}</label>
                      <textarea
                        rows={2}
                        value={questionario.diferencial}
                        onChange={e => atualizarQ('diferencial', e.target.value)}
                        placeholder={t('configuracao_ia.placeholder_diferencial')}
                        style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <label style={labelBase}>{t('configuracao_ia.label_restricoes')} <span style={{ color: C.textoMudo, fontWeight: 400 }}>({t('configuracao_ia.opcional')})</span></label>
                      <textarea
                        rows={2}
                        value={questionario.restricoes}
                        onChange={e => atualizarQ('restricoes', e.target.value)}
                        placeholder={t('configuracao_ia.placeholder_restricoes')}
                        style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <label style={labelBase}>{t('configuracao_ia.label_localizacao')} <span style={{ color: C.textoMudo, fontWeight: 400 }}>({t('configuracao_ia.opcional')})</span></label>
                      <input
                        type="text"
                        value={questionario.localizacao}
                        onChange={e => atualizarQ('localizacao', e.target.value)}
                        placeholder={t('configuracao_ia.placeholder_localizacao')}
                        style={inputBase}
                      />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: C.textoSecundario }}>
                        <input
                          type="checkbox"
                          checked={!!questionario.tem_cursos}
                          onChange={e => atualizarQ('tem_cursos', e.target.checked)}
                        />
                        {t('configuracao_ia.label_cursos')}
                      </label>
                      {questionario.tem_cursos && (
                        <textarea
                          rows={2}
                          value={questionario.cursos_descricao}
                          onChange={e => atualizarQ('cursos_descricao', e.target.value)}
                          placeholder={t('configuracao_ia.placeholder_cursos')}
                          style={{ ...inputBase, marginTop: 8, resize: 'vertical', fontFamily: 'inherit' }}
                        />
                      )}
                    </div>
                  </div>

                  <button
                    onClick={gerarConfig}
                    disabled={gerandoConfig || !personaTipo || !estiloPersona}
                    style={{
                      width: '100%', padding: '14px 18px', borderRadius: 8,
                      cursor: gerandoConfig || !personaTipo || !estiloPersona ? 'not-allowed' : 'pointer',
                      background: gerandoConfig || !personaTipo || !estiloPersona ? '#93c5fd' : C.primaria,
                      border: 'none', color: '#fff', fontSize: 15, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      transition: 'background 0.2s'
                    }}
                  >
                    <Sparkles size={17} />
                    {gerandoConfig ? t('configuracao_ia.btn_gerando') : t('configuracao_ia.btn_gerar')}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {config && instancia && jaConfigurada && (() => {
        const CAMPOS_NOTIF = [
          { id: 'nome', label: t('configuracao_ia.notif_campo_nome') },
          { id: 'cpf', label: t('configuracao_ia.notif_campo_cpf') },
          { id: 'email', label: t('configuracao_ia.notif_campo_email') },
          { id: 'contato', label: t('configuracao_ia.notif_campo_contato') },
          { id: 'data_agendamento', label: t('configuracao_ia.notif_campo_data') },
          { id: 'horario_agendamento', label: t('configuracao_ia.notif_campo_horario') },
        ];
        const camposSel = Array.isArray(config.notificacao_campos) ? config.notificacao_campos : [];
        return (
          <div style={{ ...card, marginTop: 16 }}>
            <h3 style={{ marginTop: 0, marginBottom: 6, color: C.texto, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Bell size={16} /> {t('configuracao_ia.notif_titulo')}
            </h3>
            <p style={{ fontSize: 13, color: C.textoSecundario, marginTop: 0, marginBottom: 14 }}>
              {t('configuracao_ia.notif_desc')}
            </p>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!config.notificacao_ativa}
                onChange={e => atualizarConfig('notificacao_ativa', e.target.checked)}
              />
              <span style={{ fontWeight: 600, color: C.texto }}>{t('configuracao_ia.notif_ativar')}</span>
            </label>

            {config.notificacao_ativa && (
              <>
                <label style={labelBase}>{t('configuracao_ia.notif_telefone')}</label>
                <input
                  type="tel"
                  value={config.notificacao_telefone || ''}
                  onChange={e => atualizarConfig('notificacao_telefone', e.target.value)}
                  placeholder={t('configuracao_ia.notif_telefone_ph')}
                  style={{ ...inputBase, marginBottom: 6, maxWidth: 280 }}
                />
                <p style={{ fontSize: 12, color: C.textoMudo, marginTop: 0, marginBottom: 16 }}>
                  {t('configuracao_ia.notif_telefone_ajuda')}
                </p>

                <label style={labelBase}>{t('configuracao_ia.notif_campos')}</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6, marginBottom: 14 }}>
                  {CAMPOS_NOTIF.map(c => (
                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.texto, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={camposSel.includes(c.id)}
                        onChange={() => atualizarConfig('notificacao_campos', toggleArray(camposSel, c.id))}
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              </>
            )}

            <button
              onClick={salvarNotificacao}
              disabled={salvando}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 6,
                cursor: salvando ? 'not-allowed' : 'pointer',
                background: C.primaria, border: 'none',
                color: '#fff', fontSize: 14, fontWeight: 600
              }}
            >
              <Save size={14} /> {salvando ? t('configuracao_ia.btn_salvando') : t('configuracao_ia.btn_salvar_notif')}
            </button>
          </div>
        );
      })()}

      {/* ── Integração com Agenda ── */}
      {config && instancia && jaConfigurada && agenda && (() => {
        const DIAS = [
          { n: 0, label: t('configuracao_ia.agenda_dom') },
          { n: 1, label: t('configuracao_ia.agenda_seg') },
          { n: 2, label: t('configuracao_ia.agenda_ter') },
          { n: 3, label: t('configuracao_ia.agenda_qua') },
          { n: 4, label: t('configuracao_ia.agenda_qui') },
          { n: 5, label: t('configuracao_ia.agenda_sex') },
          { n: 6, label: t('configuracao_ia.agenda_sab') },
        ];
        const diasSel = Array.isArray(agenda.dias_semana) ? agenda.dias_semana : [1,2,3,4,5];
        const updateAgenda = (campo, valor) => setAgenda(a => ({ ...a, [campo]: valor }));

  // Antecedências de lembrete (lista livre, vale para qualquer provedor de agenda).
  const lembretes = Array.isArray(agenda.lembretes_config) ? agenda.lembretes_config : [];
  const setLembretes = lista => updateAgenda('lembretes_config', lista);
  const addLembrete = () => setLembretes([...lembretes, { valor: 1, unidade: 'dias', confirma: lembretes.length === 0 }]);
  const updateLembrete = (i, campo, valor) =>
    setLembretes(lembretes.map((l, idx) => (idx === i ? { ...l, [campo]: valor } : l)));
  const removeLembrete = i => setLembretes(lembretes.filter((_, idx) => idx !== i));
        const toggleDia = (n) => {
          const next = diasSel.includes(n) ? diasSel.filter(d => d !== n) : [...diasSel, n].sort();
          updateAgenda('dias_semana', next);
        };

        return (
          <div style={{ ...card, marginTop: 16 }}>
            <h3 style={{ marginTop: 0, marginBottom: 6, color: C.texto, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Calendar size={16} /> {t('configuracao_ia.agenda_titulo')}
            </h3>
            <p style={{ fontSize: 13, color: C.textoSecundario, marginTop: 0, marginBottom: 14 }}>
              {t('configuracao_ia.agenda_desc')}
            </p>

            {/* ── Provedores diretos: Google e iCloud ── */}
            <label style={labelBase}>{t('configuracao_ia.agenda_provedor')}</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {[
                { id: null,     label: t('configuracao_ia.agenda_prov_nenhum') },
                { id: 'google', label: '📅 Google Calendar' },
                { id: 'icloud', label: '🍎 Apple iCloud' },
              ].map(p => (
                <button key={String(p.id)} onClick={() => p.id === null ? removerProvedorCalendario() : updateAgenda('provedor', p.id)}
                  style={{
                    padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    border: `2px solid ${agenda.provedor === p.id ? C.primaria : C.border}`,
                    background: agenda.provedor === p.id ? '#eff6ff' : C.card,
                    color: agenda.provedor === p.id ? C.primaria : C.texto
                  }}>
                  {p.label}
                </button>
              ))}
            </div>

            {/* ── Box: Outras integrações (Avec, Simples Dental, Clinicorp…) ── */}
            {(() => {
              const OUTRAS = [
                {
                  id: 'boxbarbearia',
                  emoji: '💈',
                  nome: 'Box Barbearia',
                  desc: 'Conecte o sistema da barbearia. A IA consulta serviços e preços, vê horário livre, marca e cancela direto na agenda.',
                  cor: '#b45309', corBg: '#fffbeb', corBorder: '#fde68a'
                },
                {
                  id: 'simplesdental',
                  emoji: '🦷',
                  nome: 'Simples Dental',
                  desc: 'Conecte sua clínica odontológica ao Simples Dental. A IA consulta disponibilidade e agenda consultas diretamente no sistema.',
                  cor: '#16a34a', corBg: '#f0fdf4', corBorder: '#bbf7d0'
                },
                {
                  id: 'avec',
                  emoji: '✂️',
                  nome: 'Avec Beauty',
                  desc: 'Integração com o sistema Avec para salões, barbearias e clínicas de estética. Agende horários diretamente pelo chat.',
                  cor: '#13305f', corBg: '#faf5ff', corBorder: '#ddd6fe'
                },
                {
                  id: 'clinicorp',
                  emoji: '🏥',
                  nome: 'Clinicorp',
                  desc: 'Conecte sua clínica ao Clinicorp. A IA verifica horários disponíveis e agenda consultas automaticamente.',
                  cor: '#0369a1', corBg: '#f0f9ff', corBorder: '#bae6fd'
                },
              ];
              const algumAtivo = OUTRAS.some(o => agenda.provedor === o.id);
              const aberto = outrasAberto || algumAtivo;

              return (
                <div style={{ marginBottom: 16 }}>
                  {/* link discreto — só mostra o box quando aberto */}
                  <button
                    onClick={() => setOutrasAberto(v => !v)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
                      display: 'flex', alignItems: 'center', gap: 6,
                      color: algumAtivo ? C.primaria : C.textoMudo,
                      fontSize: 12, fontWeight: algumAtivo ? 600 : 400
                    }}>
                    <span>{aberto ? '▾' : '▸'}</span>
                    <span>
                      {algumAtivo
                        ? `🔌 Integração ativa: ${OUTRAS.find(o => agenda.provedor === o.id)?.nome}`
                        : 'Integrar com sistema de gestão (Simples Dental, Avec, Clinicorp…)'}
                    </span>
                  </button>

                  {aberto && (
                  <div style={{ border: `1.5px solid ${C.border}`, borderRadius: 8, marginTop: 8, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {OUTRAS.map((integ, idx) => {
                      const ativo = agenda.provedor === integ.id;

                      return (
                        <div key={integ.id} style={{
                          borderTop: idx > 0 ? `1px solid ${C.border}` : 'none',
                          padding: '12px 14px',
                          background: ativo ? integ.corBg : C.card
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                            <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{integ.emoji}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700, fontSize: 13, color: C.texto }}>{integ.nome}</span>
                                {ativo && (
                                  <span style={{ fontSize: 11, fontWeight: 600, color: integ.cor, background: integ.corBg, border: `1px solid ${integ.corBorder}`, borderRadius: 10, padding: '1px 8px' }}>
                                    ● Ativo
                                  </span>
                                )}
                              </div>
                              <p style={{ fontSize: 12, color: C.textoSecundario, margin: '3px 0 8px', lineHeight: 1.5 }}>
                                {integ.desc}
                              </p>

                              {/* Configuração liberada para todos — clique para abrir os campos */}
                              <button
                                disabled={salvandoAgenda}
                                onClick={() => ativo ? desativarIntegracao() : updateAgenda('provedor', integ.id)}
                                style={{
                                  padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                                  cursor: salvandoAgenda ? 'not-allowed' : 'pointer',
                                  opacity: salvandoAgenda ? 0.6 : 1,
                                  border: `1.5px solid ${ativo ? '#fca5a5' : integ.cor}`,
                                  background: ativo ? '#fef2f2' : integ.corBg,
                                  color: ativo ? '#b91c1c' : integ.cor
                                }}>
                                {ativo ? (salvandoAgenda ? 'Salvando…' : 'Desativar') : 'Configurar'}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </div>
                  )}
                </div>
              );
            })()}

            {/* Google Calendar */}
            {agenda.provedor === 'google' && (
              <div style={{ background: '#f8fafc', border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 14 }}>
                <p style={{ margin: '0 0 10px', fontSize: 13, color: C.textoSecundario }}>
                  {t('configuracao_ia.agenda_google_desc')}
                </p>
                {agenda.google_conectado ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.ok, fontWeight: 600, fontSize: 13 }}>
                      <CheckCircle2 size={15} /> {t('configuracao_ia.agenda_google_conectado_badge')}
                    </span>
                    <button onClick={desconectarGoogle}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 5, fontSize: 12, cursor: 'pointer', background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', fontWeight: 600 }}>
                      <Unlink size={12} /> {t('configuracao_ia.agenda_google_desconectar')}
                    </button>
                  </div>
                ) : (
                  <button onClick={autorizarGoogle}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: '#4285f4', border: 'none', color: '#fff', fontWeight: 700 }}>
                    <Link size={14} /> {t('configuracao_ia.agenda_google_autorizar')}
                  </button>
                )}
                <div style={{ marginTop: 12 }}>
                  <label style={labelBase}>{t('configuracao_ia.agenda_google_calendar_id')}</label>
                  <input type="text" value={agenda.google_calendar_id || 'primary'}
                    onChange={e => updateAgenda('google_calendar_id', e.target.value)}
                    placeholder="primary"
                    style={{ ...inputBase, maxWidth: 320 }} />
                  <p style={{ fontSize: 11, color: C.textoMudo, marginTop: 4 }}>
                    {t('configuracao_ia.agenda_google_calendar_id_ajuda')}
                  </p>
                </div>
              </div>
            )}

            {/* iCloud */}
            {agenda.provedor === 'icloud' && (
              <div style={{ background: '#f8fafc', border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 14 }}>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: C.textoSecundario }}>
                  {t('configuracao_ia.agenda_icloud_desc')}
                </p>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                  <strong style={{ display: 'block', fontSize: 13, color: '#1e40af', marginBottom: 8 }}>
                    {t('configuracao_ia.agenda_icloud_passos_titulo')}
                  </strong>
                  <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#1e3a8a', lineHeight: 1.7 }}>
                    <li>{t('configuracao_ia.agenda_icloud_passo1')}</li>
                    <li>{t('configuracao_ia.agenda_icloud_passo2')}</li>
                    <li>{t('configuracao_ia.agenda_icloud_passo3')}</li>
                    <li>{t('configuracao_ia.agenda_icloud_passo4')}</li>
                  </ol>
                </div>
                <label style={labelBase}>{t('configuracao_ia.agenda_icloud_username')}</label>
                <input type="email" value={agenda.icloud_username || ''}
                  onChange={e => updateAgenda('icloud_username', e.target.value)}
                  placeholder="seuemail@icloud.com"
                  style={{ ...inputBase, marginBottom: 10, maxWidth: 320 }} />
                <label style={labelBase}>{t('configuracao_ia.agenda_icloud_password')}</label>
                <input type="password" value={agenda.icloud_password || ''}
                  onChange={e => updateAgenda('icloud_password', e.target.value)}
                  placeholder={t('configuracao_ia.agenda_icloud_password_ph')}
                  style={{ ...inputBase, marginBottom: 10, maxWidth: 320 }} />
                {renderRevelar('icloud_password')}
                <label style={labelBase}>{t('configuracao_ia.agenda_icloud_url')}</label>
                <input type="url" value={agenda.icloud_calendar_url || ''}
                  onChange={e => updateAgenda('icloud_calendar_url', e.target.value)}
                  placeholder={t('configuracao_ia.agenda_icloud_url_auto')}
                  style={{ ...inputBase, marginBottom: 4, maxWidth: 480 }} />
                <p style={{ fontSize: 11, color: C.textoMudo, marginTop: 4, marginBottom: 0 }}>
                  {t('configuracao_ia.agenda_icloud_url_ajuda')}
                </p>
              </div>
            )}

            {/* Simples Dental */}
            {agenda.provedor === 'simplesdental' && (
              <div style={{ background: '#f0fdf4', border: `1px solid #bbf7d0`, borderRadius: 8, padding: 14, marginBottom: 14 }}>
                <p style={{ margin: '0 0 10px', fontSize: 13, color: C.textoSecundario }}>
                  A IA consulta horários disponíveis e agenda consultas diretamente no Simples Dental via link público de agendamento.
                  <br /><strong>CPF e data de nascimento do paciente são obrigatórios</strong> — a IA coleta durante a conversa.
                </p>
                <p style={{ margin: '0 0 10px', fontSize: 12, background: '#fef9c3', border: '1px solid #fde047', borderRadius: 6, padding: '8px 10px', color: '#713f12' }}>
                  <strong>Como obter o token:</strong> abra seu link de agendamento no Chrome → F12 → aba Network → filtre por "Fetch/XHR" → recarregue → clique em qualquer chamada para <code>api.simplesdental.com</code> → copie o valor do header <code>x-auth-token</code>.
                </p>

                <label style={labelBase}>Sufixo do link (ex: <code>iois</code>)</label>
                <input type="text" value={agenda.simplesdental_sufixo || ''}
                  onChange={e => updateAgenda('simplesdental_sufixo', e.target.value)}
                  placeholder="iois"
                  style={inputBase} />

                <label style={{ ...labelBase, marginTop: 10 }}>Token (x-auth-token)</label>
                <input type="password" value={agenda.simplesdental_token || ''}
                  onChange={e => updateAgenda('simplesdental_token', e.target.value)}
                  placeholder="Cole o x-auth-token aqui"
                  style={inputBase} />
                {renderRevelar('simplesdental_token')}

                <button onClick={buscarProfissionaisSD} disabled={carregandoSd}
                  style={{ marginTop: 10, padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#16a34a', color: '#fff', border: 'none' }}>
                  {carregandoSd ? 'Buscando...' : '🔍 Buscar Profissionais do Link'}
                </button>

                {sdProfissionais.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <label style={{ ...labelBase, marginBottom: 6 }}>
                      Selecione os profissionais que atenderão pelo link de agendamento:
                    </label>
                    <p style={{ fontSize: 12, color: C.textoMudo, marginTop: 0, marginBottom: 8 }}>
                      Clique para adicionar. Informe a especialidade de cada um — a IA usará essa informação para orientar os pacientes.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {sdProfissionais.map(p => {
                        const id   = String(p.id || p.idCfgLinkAgendamentoProfissional || '');
                        const nome = p.profissional?.nome || p.nomeProfissional || p.nome || id;
                        const jaAdicionado = (agenda.simplesdental_profissionais || []).some(cp => String(cp.id) === id);
                        return (
                          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button onClick={() => addProfissionalSD(id, nome)} disabled={jaAdicionado}
                              style={{
                                padding: '5px 10px', borderRadius: 5, fontSize: 13, cursor: jaAdicionado ? 'default' : 'pointer',
                                background: jaAdicionado ? '#f0fdf4' : C.card,
                                border: `1px solid ${jaAdicionado ? '#86efac' : C.border}`,
                                color: jaAdicionado ? '#16a34a' : C.texto, fontWeight: 500, whiteSpace: 'nowrap'
                              }}>
                              {jaAdicionado ? '✓' : '+'} {nome}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(agenda.simplesdental_profissionais || []).length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <label style={{ ...labelBase, marginBottom: 8 }}>
                      Profissionais configurados ({(agenda.simplesdental_profissionais || []).length}):
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(agenda.simplesdental_profissionais || []).map(prof => (
                        <div key={prof.id} style={{
                          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                          background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px'
                        }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.texto, minWidth: 140, flex: '0 0 auto' }}>{prof.nome}</span>
                          <input
                            type="text"
                            value={prof.especialidade || ''}
                            onChange={e => updateEspecialidadeSD(prof.id, e.target.value)}
                            placeholder="Especialidade ou serviço (ex: Ortodontia)"
                            style={{ ...inputBase, flex: 1, minWidth: 160, padding: '5px 10px' }}
                          />
                          <button onClick={() => removeProfissionalSD(prof.id)}
                            style={{ padding: '4px 8px', borderRadius: 4, fontSize: 12, cursor: 'pointer', background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', fontWeight: 700, flexShrink: 0 }}>
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <p style={{ fontSize: 12, color: C.textoMudo, marginTop: 6 }}>
                      A IA apresentará os profissionais e especialidades ao paciente e selecionará o correto automaticamente ao agendar.
                    </p>
                  </div>
                )}
                <p style={{ fontSize: 12, color: C.textoSecundario, marginTop: 14, marginBottom: 0, paddingTop: 12, borderTop: `1px solid #bbf7d0` }}>
                  Dúvidas sobre esta integração? Envie uma mensagem para o suporte: <strong>suporte@disparaia.net</strong>
                </p>
              </div>
            )}

            {/* Avec Beauty */}
            {agenda.provedor === 'avec' && (
              <div style={{ background: '#f8fafc', border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 14 }}>
                <p style={{ margin: '0 0 10px', fontSize: 13, color: C.textoSecundario }}>
                  Conecte sua conta Avec Beauty para que a IA visualize horários disponíveis e crie agendamentos diretamente no sistema. Suporte a múltiplos profissionais.
                </p>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: '#1e3a8a', lineHeight: 1.7 }}>
                  <strong>Como configurar:</strong><br />
                  1. Informe o e-mail e senha do painel <strong>admin.avec.beauty</strong><br />
                  2. Informe o ID do salão (número na URL do painel) e o slug (ex: <em>studio-schroeder</em>)<br />
                  3. Informe a API Key (Configurações → API no painel) para relatórios<br />
                  4. Clique em "Buscar profissionais" para carregar a equipe
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                  <div>
                    <label style={labelBase}>E-mail do painel Avec</label>
                    <input type="email" value={agenda.avec_email || ''}
                      onChange={e => updateAgenda('avec_email', e.target.value)}
                      placeholder="seu@email.com"
                      style={{ ...inputBase }} />
                  </div>
                  <div>
                    <label style={labelBase}>Senha do painel Avec</label>
                    <input type="password" value={agenda.avec_password || ''}
                      onChange={e => updateAgenda('avec_password', e.target.value)}
                      placeholder="Senha de acesso"
                      style={{ ...inputBase }} />
                    {renderRevelar('avec_password')}
                  </div>
                  <div>
                    <label style={labelBase}>ID do salão</label>
                    <input type="text" value={agenda.avec_salon_id || ''}
                      onChange={e => updateAgenda('avec_salon_id', e.target.value)}
                      placeholder="Ex: 12345"
                      style={{ ...inputBase }} />
                  </div>
                  <div>
                    <label style={labelBase}>Slug do salão</label>
                    <input type="text" value={agenda.avec_slug || ''}
                      onChange={e => updateAgenda('avec_slug', e.target.value)}
                      placeholder="Ex: studio-schroeder"
                      style={{ ...inputBase }} />
                  </div>
                </div>

                <label style={{ ...labelBase, marginTop: 10 }}>API Key Avec (relatórios)</label>
                <input type="password" value={agenda.avec_api_key || ''}
                  onChange={e => updateAgenda('avec_api_key', e.target.value)}
                  placeholder="Chave de API do Avec (Configurações → API)"
                  style={{ ...inputBase, maxWidth: 380 }} />
                {renderRevelar('avec_api_key')}
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 4, marginBottom: 0 }}>
                  Servidor: <strong>api.avec.beauty</strong> (fixo, não precisa configurar)
                </p>

                <button onClick={carregarAvecDados} disabled={carregandoAvec || !agenda.avec_api_key || agenda.avec_api_key === '••••••••'}
                  style={{ marginTop: 8, marginBottom: 14, padding: '7px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600, background: '#16a34a', border: 'none', color: '#fff', opacity: (!agenda.avec_api_key || carregandoAvec) ? 0.5 : 1 }}>
                  {carregandoAvec ? 'Buscando...' : '🔍 Buscar Profissionais'}
                </button>

                {avecProfissionaisLista.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ ...labelBase, marginBottom: 6 }}>
                      Selecione os profissionais que a IA poderá agendar:
                    </label>
                    <p style={{ fontSize: 12, color: C.textoMudo, marginTop: 0, marginBottom: 8 }}>
                      Clique para adicionar. Informe a especialidade/serviço — a IA usará para recomendar ao cliente.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {avecProfissionaisLista.map(p => {
                        const id = String(p.id || '');
                        const nome = p.nome || id;
                        const jaAdicionado = (agenda.avec_profissionais || []).some(x => String(x.id) === id);
                        return (
                          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button onClick={() => addProfissionalAvec(id, nome)} disabled={jaAdicionado}
                              style={{
                                padding: '5px 10px', borderRadius: 5, fontSize: 13, cursor: jaAdicionado ? 'default' : 'pointer',
                                background: jaAdicionado ? '#f0fdf4' : C.card,
                                border: `1px solid ${jaAdicionado ? '#86efac' : C.border}`,
                                color: jaAdicionado ? '#166534' : C.texto, fontWeight: 500, whiteSpace: 'nowrap'
                              }}>
                              {jaAdicionado ? '✓' : '+'} {nome}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(agenda.avec_profissionais || []).length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ ...labelBase, marginBottom: 8 }}>
                      Profissionais configurados ({(agenda.avec_profissionais || []).length}):
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(agenda.avec_profissionais || []).map(prof => (
                        <div key={prof.id} style={{
                          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                          background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px'
                        }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.texto, minWidth: 140, flex: '0 0 auto' }}>{prof.nome}</span>
                          <input type="text" value={prof.especialidade || ''}
                            onChange={e => updateEspecialidadeAvec(prof.id, e.target.value)}
                            placeholder="Especialidade/serviço (ex: Colorista)"
                            style={{ ...inputBase, flex: 1, minWidth: 160, padding: '5px 10px' }} />
                          <button onClick={() => removeProfissionalAvec(prof.id)}
                            style={{ padding: '4px 8px', borderRadius: 4, fontSize: 12, cursor: 'pointer', background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', fontWeight: 700, flexShrink: 0 }}>
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <p style={{ fontSize: 12, color: C.textoMudo, marginTop: 6 }}>
                      A IA apresentará os profissionais e especialidades ao cliente e selecionará o correto automaticamente.
                    </p>
                  </div>
                )}

                {avecServicos.length > 0 && (
                  <div style={{ marginBottom: 4 }}>
                    <label style={labelBase}>Serviço padrão (opcional)</label>
                    <select value={agenda.avec_servico_id || ''}
                      onChange={e => updateAgenda('avec_servico_id', e.target.value)}
                      style={{ ...inputBase, maxWidth: 320 }}>
                      <option value="">Qualquer serviço</option>
                      {avecServicos.map(s => (
                        <option key={s.id} value={s.id}>{s.nome} ({s.duracao} min)</option>
                      ))}
                    </select>
                  </div>
                )}
                <p style={{ fontSize: 12, color: C.textoSecundario, marginTop: 14, marginBottom: 0, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                  Dúvidas sobre esta integração? Envie uma mensagem para o suporte: <strong>suporte@disparaia.net</strong>
                </p>
              </div>
            )}

            {/* Clinicorp */}
            {agenda.provedor === 'boxbarbearia' && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 14, marginBottom: 14 }}>
                <p style={{ margin: '0 0 10px', fontSize: 13, color: C.textoSecundario }}>
                  A IA lê os serviços e preços da barbearia, consulta horário livre, marca e cancela direto na agenda.
                  <br />O cliente é identificado pelo <strong>nome e telefone</strong> — não pede CPF nem data de nascimento.
                </p>
                <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 10px', marginBottom: 12, fontSize: 12, color: '#78350f', lineHeight: 1.6 }}>
                  <strong>Onde pegar:</strong> a URL é o endereço do sistema da barbearia; o token é o <code>AGENT_API_TOKEN</code> cadastrado nas variáveis de ambiente dela.
                </div>

                <label style={labelBase}>URL da API</label>
                <input type="text" value={agenda.boxbarbearia_url || ''}
                  onChange={e => updateAgenda('boxbarbearia_url', e.target.value)}
                  placeholder="https://suabarbearia.vercel.app"
                  style={inputBase} />

                <label style={labelBase}>Token da integração</label>
                <input type="password" value={agenda.boxbarbearia_token || ''}
                  onChange={e => updateAgenda('boxbarbearia_token', e.target.value)}
                  placeholder="AGENT_API_TOKEN"
                  style={inputBase} />
                {renderRevelar('boxbarbearia_token')}

                <label style={labelBase}>Serviço padrão</label>
                {boxCatalogo?.servicos?.length > 0 ? (
                  <select value={agenda.boxbarbearia_servico_padrao || ''}
                    onChange={e => updateAgenda('boxbarbearia_servico_padrao', e.target.value)}
                    style={inputBase}>
                    <option value="">Selecione</option>
                    {boxCatalogo.servicos.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.nome} — R$ {s.preco} ({s.duracaoMinutos}min)
                      </option>
                    ))}
                  </select>
                ) : (
                  <input type="text" value={agenda.boxbarbearia_servico_padrao || ''}
                    onChange={e => updateAgenda('boxbarbearia_servico_padrao', e.target.value)}
                    placeholder="id do serviço (use o botão abaixo para listar)"
                    style={inputBase} />
                )}
                <p style={{ fontSize: 12, color: C.textoMudo, margin: '4px 0 0', lineHeight: 1.4 }}>
                  Usado para consultar horários quando a conversa ainda não deixou claro o serviço.
                  A duração dele define o que cabe na agenda — <strong>escolha o serviço mais longo</strong> para nunca
                  oferecer um horário que não comporta o atendimento.
                </p>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                  <button onClick={buscarCatalogoBox} disabled={carregandoBox}
                    style={{ padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#b45309', color: '#fff', border: 'none' }}>
                    {carregandoBox ? 'Buscando...' : '📋 Buscar serviços e profissionais'}
                  </button>
                  <button onClick={rodarDiagnosticoBox} disabled={carregandoBoxDiag}
                    style={{ padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#92400e', color: '#fff', border: 'none' }}>
                    {carregandoBoxDiag ? 'Verificando...' : '🩺 Diagnóstico da integração'}
                  </button>
                </div>

                {boxCatalogo?.profissionais?.length > 0 && (
                  <p style={{ fontSize: 12, color: C.textoMudo, margin: '10px 0 0' }}>
                    Profissionais ativos: {boxCatalogo.profissionais.map(p => p.nome).join(' · ')}
                  </p>
                )}

                {boxDiagnostico && (
                  <div style={{ marginTop: 12, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
                    {boxDiagnostico.passos.map(passo => (
                      <div key={passo.chave} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                        <span style={{ fontSize: 14 }}>{passo.ok ? '✅' : '❌'}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.texto }}>{passo.titulo}</div>
                          <div style={{ fontSize: 12, color: C.textoMudo }}>{passo.detalhe}</div>
                          {passo.dados?.primeiros?.length > 0 && (
                            <div style={{ fontSize: 12, color: C.textoMudo, marginTop: 2 }}>
                              {passo.dados.primeiros.join(' · ')}
                            </div>
                          )}
                          {passo.dados?.servicos?.length > 0 && (
                            <div style={{ fontSize: 12, color: C.textoMudo, marginTop: 2 }}>
                              {passo.dados.servicos.join(' · ')}
                            </div>
                          )}
                          {/* Uma LINHA por serviço: a duração define o que cabe, então
                              "Corte" e "Corte + Barba" têm agendas diferentes no mesmo
                              dia. Juntar tudo numa linha só esconderia justamente isso. */}
                          {passo.dados?.porServico?.length > 0 && (
                            <div style={{ marginTop: 4 }}>
                              {passo.dados.porServico.map((linha, i) => (
                                <div key={i} style={{ fontSize: 12, color: C.textoMudo, lineHeight: 1.6 }}>
                                  {linha}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {boxDiagnostico.alertas?.map((a, i) => (
                      <div key={i} style={{ marginTop: 8, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#78350f', lineHeight: 1.5 }}>
                        ⚠️ {a}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {agenda.provedor === 'clinicorp' && (
              <div style={{ background: '#f0f9ff', border: `1px solid #bae6fd`, borderRadius: 8, padding: 14, marginBottom: 14 }}>
                <p style={{ margin: '0 0 10px', fontSize: 13, color: C.textoSecundario }}>
                  A IA consulta horários disponíveis e agenda consultas diretamente no Clinicorp.
                  <br />O paciente é reconhecido pelo <strong>número de WhatsApp da conversa</strong> — quem já tem cadastro agenda sem informar CPF. A IA só pede CPF e data de nascimento quando precisa criar o cadastro.
                </p>
                <div style={{ background: '#e0f2fe', border: '1px solid #7dd3fc', borderRadius: 6, padding: '8px 10px', marginBottom: 12, fontSize: 12, color: '#0c4a6e', lineHeight: 1.6 }}>
                  <strong>Como obter as credenciais:</strong> painel Clinicorp → Configurações → Integrações → API. A API usa usuário e senha (HTTP Basic).
                </div>

                <label style={labelBase}>Usuário da API</label>
                <input type="text" value={agenda.clinicorp_usuario || ''}
                  onChange={e => updateAgenda('clinicorp_usuario', e.target.value)}
                  placeholder="ex.: minhaclinica"
                  style={inputBase} />

                <label style={labelBase}>Senha da API</label>
                <input type="password" value={agenda.clinicorp_token || ''}
                  onChange={e => updateAgenda('clinicorp_token', e.target.value)}
                  placeholder="senha da API do Clinicorp"
                  style={inputBase} />
                {renderRevelar('clinicorp_token')}

                <label style={labelBase}>ID do assinante <span style={{ fontWeight: 400, color: C.textoMudo }}>(opcional — se vazio, usa o usuário)</span></label>
                <input type="text" value={agenda.clinicorp_subscriber_id || ''}
                  onChange={e => updateAgenda('clinicorp_subscriber_id', e.target.value)}
                  placeholder="subscriber_id"
                  style={inputBase} />

                <label style={labelBase}>Clínica</label>
                {cpClinicas.length > 0 ? (
                  <select value={agenda.clinicorp_clinic_id || ''}
                    onChange={e => updateAgenda('clinicorp_clinic_id', e.target.value)}
                    style={inputBase}>
                    <option value="">Selecione a clínica</option>
                    {cpClinicas.map(c => (
                      <option key={c.id} value={c.id}>{c.nome}{c.endereco ? ` — ${c.endereco}` : ''}</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" value={agenda.clinicorp_clinic_id || ''}
                    onChange={e => updateAgenda('clinicorp_clinic_id', e.target.value)}
                    placeholder="ID da clínica (use o botão abaixo para listar)"
                    style={inputBase} />
                )}

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={agenda.orcamento_followup_ativo === true}
                    onChange={e => updateAgenda('orcamento_followup_ativo', e.target.checked)}
                    style={{ marginTop: 3 }} />
                  <span style={{ fontWeight: 600, color: C.texto, fontSize: 13 }}>
                    Reengajar pacientes com orçamento em aberto
                  </span>
                </label>
                <p style={{ fontSize: 12, color: C.textoMudo, margin: '4px 0 0 26px', lineHeight: 1.4 }}>
                  A IA envia uma mensagem para quem recebeu orçamento e não fechou, com o valor total e o convite para tirar dúvidas.
                  Não alcança quem já tem consulta marcada nem conversa que você assumiu pelo celular. Desligado por padrão.
                </p>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <button onClick={buscarClinicasCP} disabled={carregandoCpClinicas}
                    style={{ padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#0369a1', color: '#fff', border: 'none' }}>
                    {carregandoCpClinicas ? 'Buscando...' : '🏥 Buscar Clínicas'}
                  </button>
                  <button onClick={buscarProfissionaisCP} disabled={carregandoCp}
                    style={{ padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#0369a1', color: '#fff', border: 'none' }}>
                    {carregandoCp ? 'Buscando...' : '🔍 Buscar Profissionais'}
                  </button>
                  <button onClick={rodarDiagnosticoCP} disabled={carregandoCpDiag}
                    style={{ padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#075985', color: '#fff', border: 'none' }}>
                    {carregandoCpDiag ? 'Verificando...' : '🩺 Diagnóstico da integração'}
                  </button>
                </div>

                {cpDiagnostico && (
                  <div style={{ marginTop: 12, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
                    {cpDiagnostico.passos.map(passo => (
                      <div key={passo.chave} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                        <span style={{ fontSize: 14 }}>{passo.ok ? '✅' : '❌'}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.texto }}>{passo.titulo}</div>
                          <div style={{ fontSize: 12, color: C.textoMudo }}>{passo.detalhe}</div>
                          {passo.dados?.primeiros?.length > 0 && (
                            <div style={{ fontSize: 12, color: C.textoMudo, marginTop: 2 }}>
                              Próximos: {passo.dados.primeiros.join(' · ')}
                            </div>
                          )}
                          {passo.dados?.slotCru && (
                            <div style={{ fontSize: 11, color: C.textoMudo, marginTop: 2, fontFamily: 'monospace' }}>
                              resposta da API: {JSON.stringify(passo.dados.slotCru)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {cpDiagnostico.alertas?.map((a, i) => (
                      <div key={i} style={{ marginTop: 8, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#78350f', lineHeight: 1.5 }}>
                        ⚠️ {a}
                      </div>
                    ))}
                  </div>
                )}

                {/* Agendamento de teste — escreve na agenda, então fica separado e com aviso */}
                <div style={{ marginTop: 12, border: `1px dashed ${C.border}`, borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.texto, marginBottom: 4 }}>
                    Agendamento de teste
                  </div>
                  <p style={{ fontSize: 12, color: C.textoMudo, margin: '0 0 8px', lineHeight: 1.4 }}>
                    Cria uma consulta real no primeiro horário livre para conferir se o dia e a hora chegam corretos ao Clinicorp.
                    Você cancela em seguida, aqui mesmo.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input type="text" value={cpTelTeste}
                      onChange={e => setCpTelTeste(e.target.value)}
                      placeholder="Telefone com DDD"
                      style={{ ...inputBase, width: 200, marginBottom: 0 }} />
                    <button onClick={rodarAgendamentoTesteCP} disabled={carregandoCpAgTeste}
                      style={{ padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: C.card, border: `1px solid ${C.border}`, color: C.texto }}>
                      {carregandoCpAgTeste ? 'Agendando...' : '📅 Criar agendamento de teste'}
                    </button>
                  </div>

                  {cpAgTeste && (
                    <div style={{ marginTop: 10, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#14532d', lineHeight: 1.6 }}>
                      <div><strong>Enviamos:</strong> {cpAgTeste.enviado.data} às {cpAgTeste.enviado.hora} — {cpAgTeste.enviado.profissional}</div>
                      <div>
                        <strong>O Clinicorp registrou:</strong>{' '}
                        {cpAgTeste.registrado
                          ? `${cpAgTeste.registrado.data} às ${cpAgTeste.registrado.hora}`
                          : 'não foi possível reler — confira no painel do Clinicorp'}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        Paciente: {cpAgTeste.paciente.nome} {cpAgTeste.paciente.novo ? '(cadastro criado agora)' : '(já existia)'} · consulta #{cpAgTeste.consulta_id}
                      </div>
                      <button onClick={cancelarAgendamentoTesteCP}
                        style={{ marginTop: 8, padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: '#fff', border: '1px solid #86efac', color: '#14532d' }}>
                        Cancelar este agendamento
                      </button>
                    </div>
                  )}
                </div>

                {cpProfissionais.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <label style={{ ...labelBase, marginBottom: 6 }}>
                      Selecione os profissionais que a IA poderá agendar:
                    </label>
                    <p style={{ fontSize: 12, color: C.textoMudo, marginTop: 0, marginBottom: 8 }}>
                      Clique para adicionar. Informe a especialidade — a IA usará para orientar os pacientes.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {cpProfissionais.map(p => {
                        const id   = String(p.id || '');
                        const nome = p.nome || id;
                        const jaAdicionado = (agenda.clinicorp_profissionais || []).some(cp => String(cp.id) === id);
                        return (
                          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button onClick={() => addProfissionalCP(id, nome)} disabled={jaAdicionado}
                              style={{
                                padding: '5px 10px', borderRadius: 5, fontSize: 13, cursor: jaAdicionado ? 'default' : 'pointer',
                                background: jaAdicionado ? '#f0f9ff' : C.card,
                                border: `1px solid ${jaAdicionado ? '#7dd3fc' : C.border}`,
                                color: jaAdicionado ? '#0369a1' : C.texto, fontWeight: 500, whiteSpace: 'nowrap'
                              }}>
                              {jaAdicionado ? '✓' : '+'} {nome}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(agenda.clinicorp_profissionais || []).length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <label style={{ ...labelBase, marginBottom: 8 }}>
                      Profissionais configurados ({(agenda.clinicorp_profissionais || []).length}):
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(agenda.clinicorp_profissionais || []).map(prof => (
                        <div key={prof.id} style={{
                          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                          background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px'
                        }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.texto, minWidth: 140, flex: '0 0 auto' }}>{prof.nome}</span>
                          <input type="text" value={prof.especialidade || ''}
                            onChange={e => updateEspecialidadeCP(prof.id, e.target.value)}
                            placeholder="Especialidade (ex: Cardiologia)"
                            style={{ ...inputBase, flex: 1, minWidth: 160, padding: '5px 10px' }} />
                          <button onClick={() => removeProfissionalCP(prof.id)}
                            style={{ padding: '4px 8px', borderRadius: 4, fontSize: 12, cursor: 'pointer', background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', fontWeight: 700, flexShrink: 0 }}>
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <p style={{ fontSize: 12, color: C.textoMudo, marginTop: 6 }}>
                      A IA apresentará os profissionais e especialidades ao paciente e selecionará o correto automaticamente.
                    </p>
                  </div>
                )}
                <p style={{ fontSize: 12, color: C.textoSecundario, marginTop: 14, marginBottom: 0, paddingTop: 12, borderTop: `1px solid #bae6fd` }}>
                  Dúvidas sobre esta integração? Envie uma mensagem para o suporte: <strong>suporte@disparaia.net</strong>
                </p>
              </div>
            )}

            {agenda.provedor && (
              <>
                {/* Dias e horários */}
                <label style={{ ...labelBase, marginTop: 4 }}>{t('configuracao_ia.agenda_dias')}</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                  {DIAS.map(d => (
                    <button key={d.n} onClick={() => toggleDia(d.n)}
                      style={{
                        padding: '5px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontWeight: 600,
                        border: `2px solid ${diasSel.includes(d.n) ? C.primaria : C.border}`,
                        background: diasSel.includes(d.n) ? '#eff6ff' : C.card,
                        color: diasSel.includes(d.n) ? C.primaria : C.textoSecundario
                      }}>
                      {d.label}
                    </button>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={labelBase}>{t('configuracao_ia.agenda_hora_inicio')}</label>
                    <input type="time" value={agenda.hora_inicio || '08:00'}
                      onChange={e => updateAgenda('hora_inicio', e.target.value)}
                      style={{ ...inputBase }} />
                  </div>
                  <div>
                    <label style={labelBase}>{t('configuracao_ia.agenda_hora_fim')}</label>
                    <input type="time" value={agenda.hora_fim || '18:00'}
                      onChange={e => updateAgenda('hora_fim', e.target.value)}
                      style={{ ...inputBase }} />
                  </div>
                  <div>
                    <label style={labelBase}>{t('configuracao_ia.agenda_duracao')}</label>
                    <select value={agenda.duracao_minutos || 60}
                      onChange={e => updateAgenda('duracao_minutos', parseInt(e.target.value, 10))}
                      style={{ ...inputBase }}>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>60 min</option>
                      <option value={90}>90 min</option>
                      <option value={120}>2 horas</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelBase}>{t('configuracao_ia.agenda_timezone')}</label>
                    <select value={agenda.timezone || 'America/Sao_Paulo'}
                      onChange={e => updateAgenda('timezone', e.target.value)}
                      style={{ ...inputBase }}>
                      <option value="America/Sao_Paulo">São Paulo (BRT)</option>
                      <option value="America/Manaus">Manaus (AMT)</option>
                      <option value="America/Belem">Belém (BRT)</option>
                      <option value="America/Fortaleza">Fortaleza (BRT)</option>
                      <option value="America/Recife">Recife (BRT)</option>
                      <option value="America/Porto_Velho">Porto Velho (AMT)</option>
                      <option value="America/Boa_Vista">Boa Vista (AMT)</option>
                      <option value="America/Noronha">Fernando de Noronha (FNT)</option>
                      <option value="America/Rio_Branco">Rio Branco (ACT)</option>
                    </select>
                  </div>
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!agenda.ativo}
                    onChange={e => updateAgenda('ativo', e.target.checked)} />
                  <span style={{ fontWeight: 600, color: C.texto }}>{t('configuracao_ia.agenda_ativar')}</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={agenda.agenda_confirmacao_ativa !== false}
                    onChange={e => updateAgenda('agenda_confirmacao_ativa', e.target.checked)}
                    style={{ marginTop: 3 }} />
                  <span style={{ fontWeight: 600, color: C.texto }}>{t('configuracao_ia.agenda_confirmacao_ativar')}</span>
                </label>
                <p style={{ fontSize: 12, color: C.textoMudo, margin: '0 0 14px 26px', lineHeight: 1.4 }}>
                  {t('configuracao_ia.agenda_confirmacao_ajuda')}
                </p>

                {/* ── Antecedências de lembrete — comum a TODOS os provedores ── */}
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, color: C.texto, marginBottom: 4 }}>
                    {t('configuracao_ia.lembretes_titulo')}
                  </div>
                  <p style={{ fontSize: 12, color: C.textoMudo, margin: '0 0 12px', lineHeight: 1.4 }}>
                    {t('configuracao_ia.lembretes_ajuda')}
                  </p>

                  {lembretes.map((l, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                      <input type="number" min="1" value={l.valor ?? ''}
                        onChange={e => updateLembrete(i, 'valor', parseInt(e.target.value, 10) || '')}
                        style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 14 }} />
                      <select value={l.unidade || 'dias'}
                        onChange={e => updateLembrete(i, 'unidade', e.target.value)}
                        style={{ padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 14 }}>
                        <option value="dias">{t('configuracao_ia.lembretes_dias')}</option>
                        <option value="horas">{t('configuracao_ia.lembretes_horas')}</option>
                      </select>
                      <span style={{ fontSize: 13, color: C.textoMudo }}>{t('configuracao_ia.lembretes_antes')}</span>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: C.texto }}>
                        <input type="checkbox" checked={l.confirma === true}
                          onChange={e => updateLembrete(i, 'confirma', e.target.checked)} />
                        {t('configuracao_ia.lembretes_confirma')}
                      </label>
                      <button onClick={() => removeLembrete(i)} title={t('configuracao_ia.lembretes_remover')}
                        style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.textoMudo, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>
                    </div>
                  ))}

                  <button onClick={addLembrete}
                    style={{ marginTop: 4, padding: '6px 12px', borderRadius: 6, border: `1px dashed ${C.border}`, background: 'transparent', color: C.texto, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    + {t('configuracao_ia.lembretes_add')}
                  </button>

                  {lembretes.length > 0 && (
                    <>
                      <p style={{ fontSize: 12, color: C.textoMudo, margin: '12px 0 0', lineHeight: 1.4 }}>
                        {t('configuracao_ia.lembretes_confirma_ajuda')}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                        <span style={{ fontSize: 13, color: C.texto, fontWeight: 600 }}>
                          {t('configuracao_ia.lembretes_janela_de')}
                        </span>
                        <input type="time" value={agenda.lembretes_janela_inicio || '08:00'}
                          onChange={e => updateAgenda('lembretes_janela_inicio', e.target.value)}
                          style={{ padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 14 }} />
                        <span style={{ fontSize: 13, color: C.textoMudo }}>
                          {t('configuracao_ia.lembretes_janela_ate')}
                        </span>
                        <input type="time" value={agenda.lembretes_janela_fim || '20:00'}
                          onChange={e => updateAgenda('lembretes_janela_fim', e.target.value)}
                          style={{ padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 14 }} />
                      </div>
                      <p style={{ fontSize: 12, color: C.textoMudo, margin: '6px 0 0', lineHeight: 1.4 }}>
                        {t('configuracao_ia.lembretes_janela')}
                      </p>

                      {/* Contradição silenciosa: com antecedências salvas, o caminho
                          antigo para de cobrir esta instância; se o envio estiver
                          desmarcado aqui, ninguém envia. Era o caso do iois. */}
                      {agenda.agenda_confirmacao_ativa === false && (
                        <div style={{ marginTop: 12, padding: 10, borderRadius: 6, background: '#7f1d1d22', border: '1px solid #ef4444', fontSize: 12, color: C.texto, lineHeight: 1.5 }}>
                          ⚠️ {t('configuracao_ia.lembretes_conflito')}
                        </div>
                      )}

                      <button onClick={rodarDiagnosticoLembretes} disabled={carregandoDiagLembretes}
                        style={{ marginTop: 12, padding: '6px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, cursor: carregandoDiagLembretes ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                        🩺 {carregandoDiagLembretes ? t('configuracao_ia.lembretes_diag_rodando') : t('configuracao_ia.lembretes_diag')}
                      </button>

                      {diagLembretes && (
                        <div style={{ marginTop: 10, padding: 10, borderRadius: 6, background: C.bg, border: `1px solid ${C.border}`, fontSize: 12, color: C.texto, lineHeight: 1.6 }}>
                          {(diagLembretes.passos || []).map((p, i) => (
                            <div key={i} style={{ marginBottom: 6 }}>
                              <span>{p.ok ? '✅' : '❌'} <strong>{p.titulo}</strong></span>
                              {p.detalhe && (
                                <div style={{ color: C.textoSecundario, whiteSpace: 'pre-wrap', marginLeft: 20 }}>{p.detalhe}</div>
                              )}
                            </div>
                          ))}
                          {(diagLembretes.alertas || []).map((a, i) => (
                            <div key={`a${i}`} style={{ marginTop: 8, color: C.alerta, fontWeight: 600 }}>⚠️ {a}</div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* ─── Reativação de clientes inativos ───────────────────────
                    OPT-IN: nada sai enquanto a caixa não for marcada. É mensagem
                    para o cliente DO cliente, então ligar por padrão seria decidir
                    por ele. O período tem teto de 60 dias porque é esse o alcance
                    da agenda do período no sistema do tenant. */}
                <div style={{ marginTop: 16, padding: 12, borderRadius: 8, background: C.bg, border: `1px solid ${C.border}` }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: C.texto }}>
                    <input type="checkbox" checked={agenda.reativacao_ativa === true}
                      onChange={e => updateAgenda('reativacao_ativa', e.target.checked)} />
                    🔁 {t('configuracao_ia.reativacao_titulo')}
                  </label>
                  <p style={{ fontSize: 12, color: C.textoSecundario, margin: '6px 0 0 24px' }}>
                    {t('configuracao_ia.reativacao_desc')}
                  </p>

                  {agenda.reativacao_ativa === true && (
                    <>
                      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <label style={{ fontSize: 12, color: C.textoSecundario }}>
                          {t('configuracao_ia.reativacao_dias_label')}
                        </label>
                        <input type="number" min={7} max={60} step={1}
                          value={agenda.reativacao_dias ?? 30}
                          onChange={e => updateAgenda('reativacao_dias', e.target.value)}
                          style={{ width: 80, padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 13 }} />
                        <span style={{ fontSize: 12, color: C.textoMudo }}>{t('configuracao_ia.reativacao_dias_hint')}</span>
                      </div>

                      <div style={{ marginTop: 12 }}>
                        <label style={{ display: 'block', fontSize: 12, color: C.textoSecundario, marginBottom: 4 }}>
                          {t('configuracao_ia.reativacao_texto_label')}
                        </label>
                        <textarea rows={4} maxLength={1200}
                          placeholder={t('configuracao_ia.reativacao_texto_ph')}
                          value={agenda.reativacao_texto || ''}
                          onChange={e => updateAgenda('reativacao_texto', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 13, resize: 'vertical' }} />
                        <p style={{ fontSize: 11, color: C.textoMudo, marginTop: 4 }}>
                          {t('configuracao_ia.reativacao_texto_hint')}
                        </p>
                      </div>

                      <button onClick={rodarDiagnosticoReativacao} disabled={carregandoDiagReativacao}
                        style={{ marginTop: 12, padding: '6px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 12, cursor: carregandoDiagReativacao ? 'not-allowed' : 'pointer' }}>
                        🩺 {carregandoDiagReativacao ? t('configuracao_ia.reativacao_diag_rodando') : t('configuracao_ia.reativacao_diag')}
                      </button>

                      {diagReativacao && (
                        <div style={{ marginTop: 10, padding: 10, borderRadius: 6, background: C.card, border: `1px solid ${C.border}`, fontSize: 12, color: C.texto }}>
                          {(diagReativacao.passos || []).map((p, i) => (
                            <div key={i} style={{ marginBottom: 6 }}>
                              <span>{p.ok ? '✅' : '❌'} <strong>{p.titulo}</strong></span>
                              {p.detalhe && (
                                <div style={{ color: C.textoSecundario, whiteSpace: 'pre-wrap', marginLeft: 20 }}>{p.detalhe}</div>
                              )}
                            </div>
                          ))}
                          {(diagReativacao.alertas || []).map((a, i) => (
                            <div key={`ra${i}`} style={{ marginTop: 8, color: C.alerta, fontWeight: 600 }}>⚠️ {a}</div>
                          ))}
                          {(diagReativacao.previa || []).length > 0 && (
                            <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('configuracao_ia.reativacao_previa')}</div>
                              {diagReativacao.previa.map((c, i) => (
                                <div key={`p${i}`} style={{ color: c.sairia ? C.texto : C.textoMudo, marginBottom: 2 }}>
                                  {c.sairia ? '•' : '–'} {c.nome} · {t('configuracao_ia.reativacao_sem_voltar', { dias: c.diasSemVoltar })}
                                  {c.motivo ? ` — ${c.motivo}` : ''}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* ─── Pós-atendimento ───────────────────────────────────────
                    OPT-IN, como a reativação e pelo mesmo motivo. A janela de
                    horário NÃO é pedida aqui: o motor reusa a dos lembretes, que
                    responde a mesma pergunta ("quando posso falar com o cliente?").
                    Pedir dois pares de campos criaria duas fontes de verdade. */}
                <div style={{ marginTop: 16, padding: 12, borderRadius: 8, background: C.bg, border: `1px solid ${C.border}` }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: C.texto }}>
                    <input type="checkbox" checked={agenda.pos_atendimento_ativo === true}
                      onChange={e => updateAgenda('pos_atendimento_ativo', e.target.checked)} />
                    🙏 {t('configuracao_ia.pos_atend_titulo')}
                  </label>
                  <p style={{ fontSize: 12, color: C.textoSecundario, margin: '6px 0 0 24px' }}>
                    {t('configuracao_ia.pos_atend_desc')}
                  </p>

                  {agenda.pos_atendimento_ativo === true && (
                    <>
                      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <label style={{ fontSize: 12, color: C.textoSecundario }}>
                          {t('configuracao_ia.pos_atend_horas_label')}
                        </label>
                        <input type="number" min={1} max={48} step={1}
                          value={agenda.pos_atendimento_horas ?? 3}
                          onChange={e => updateAgenda('pos_atendimento_horas', e.target.value)}
                          style={{ width: 80, padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 13 }} />
                        <span style={{ fontSize: 12, color: C.textoMudo }}>{t('configuracao_ia.pos_atend_horas_hint')}</span>
                      </div>

                      <div style={{ marginTop: 12 }}>
                        <label style={{ display: 'block', fontSize: 12, color: C.textoSecundario, marginBottom: 4 }}>
                          {t('configuracao_ia.pos_atend_texto_label')}
                        </label>
                        <textarea rows={4} maxLength={1200}
                          placeholder={t('configuracao_ia.pos_atend_texto_ph')}
                          value={agenda.pos_atendimento_texto || ''}
                          onChange={e => updateAgenda('pos_atendimento_texto', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 13, resize: 'vertical' }} />
                        <p style={{ fontSize: 11, color: C.textoMudo, marginTop: 4 }}>
                          {t('configuracao_ia.pos_atend_texto_hint')}
                        </p>
                      </div>

                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: C.texto, marginTop: 12 }}>
                        <input type="checkbox" checked={agenda.pos_atendimento_avaliacao === true}
                          onChange={e => updateAgenda('pos_atendimento_avaliacao', e.target.checked)} />
                        {t('configuracao_ia.pos_atend_avaliacao')}
                      </label>
                      {agenda.pos_atendimento_avaliacao === true && (
                        <div style={{ marginTop: 8, marginLeft: 24 }}>
                          <input type="url" maxLength={500}
                            placeholder={t('configuracao_ia.pos_atend_link_ph')}
                            value={agenda.pos_atendimento_link || ''}
                            onChange={e => updateAgenda('pos_atendimento_link', e.target.value)}
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 13 }} />
                          <p style={{ fontSize: 11, color: C.textoMudo, marginTop: 4 }}>
                            {t('configuracao_ia.pos_atend_link_hint')}
                          </p>
                          <p style={{ fontSize: 11, color: C.textoMudo, marginTop: 4 }}>
                            {t('configuracao_ia.pos_atend_avaliacao_hint')}
                          </p>
                        </div>
                      )}

                      <button onClick={rodarDiagnosticoPosAtend} disabled={carregandoDiagPosAtend}
                        style={{ marginTop: 12, padding: '6px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 12, cursor: carregandoDiagPosAtend ? 'not-allowed' : 'pointer' }}>
                        🩺 {carregandoDiagPosAtend ? t('configuracao_ia.pos_atend_diag_rodando') : t('configuracao_ia.pos_atend_diag')}
                      </button>

                      {diagPosAtend && (
                        <div style={{ marginTop: 10, padding: 10, borderRadius: 6, background: C.card, border: `1px solid ${C.border}`, fontSize: 12, color: C.texto }}>
                          {(diagPosAtend.passos || []).map((p, i) => (
                            <div key={`pp${i}`} style={{ marginBottom: 6 }}>
                              <span>{p.ok ? '✅' : '❌'} <strong>{p.titulo}</strong></span>
                              {p.detalhe && (
                                <div style={{ color: C.textoSecundario, whiteSpace: 'pre-wrap', marginLeft: 20 }}>{p.detalhe}</div>
                              )}
                            </div>
                          ))}
                          {(diagPosAtend.alertas || []).map((a, i) => (
                            <div key={`pa${i}`} style={{ marginTop: 8, color: C.alerta, fontWeight: 600 }}>⚠️ {a}</div>
                          ))}
                          {(diagPosAtend.previa || []).length > 0 && (
                            <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('configuracao_ia.pos_atend_previa')}</div>
                              {diagPosAtend.previa.map((c, i) => (
                                <div key={`pv${i}`} style={{ color: c.sairia ? C.texto : C.textoMudo, marginBottom: 2 }}>
                                  {c.sairia ? '•' : '–'} {c.nome} · {c.visita}
                                  {c.sairia && c.avaliacao ? ` · ${t('configuracao_ia.pos_atend_com_avaliacao')}` : ''}
                                  {c.motivo ? ` — ${c.motivo}` : ''}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* ─── Lista de espera ───────────────────────────────────────
                    OPT-IN. A espera é registrada DETERMINISTICAMENTE: quando o
                    cliente pede um dia específico e aquele dia volta sem vaga, o
                    código já sabe disso com prova autoritativa (é o mesmo fato que
                    afirma no prompt "o dia X não tem horário livre"). Não depende
                    do modelo, então não há espera inventada. */}
                <div style={{ marginTop: 16, padding: 12, borderRadius: 8, background: C.bg, border: `1px solid ${C.border}` }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: C.texto }}>
                    <input type="checkbox" checked={agenda.espera_ativa === true}
                      onChange={e => updateAgenda('espera_ativa', e.target.checked)} />
                    ⏳ {t('configuracao_ia.espera_titulo')}
                  </label>
                  <p style={{ fontSize: 12, color: C.textoSecundario, margin: '6px 0 0 24px' }}>
                    {t('configuracao_ia.espera_desc')}
                  </p>

                  {agenda.espera_ativa === true && (
                    <>
                      <div style={{ marginTop: 12 }}>
                        <label style={{ display: 'block', fontSize: 12, color: C.textoSecundario, marginBottom: 4 }}>
                          {t('configuracao_ia.espera_texto_label')}
                        </label>
                        <textarea rows={2} maxLength={500}
                          placeholder={t('configuracao_ia.espera_texto_ph')}
                          value={agenda.espera_texto || ''}
                          onChange={e => updateAgenda('espera_texto', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 13, resize: 'vertical' }} />
                        <p style={{ fontSize: 11, color: C.textoMudo, marginTop: 4 }}>
                          {t('configuracao_ia.espera_texto_hint')}
                        </p>
                      </div>

                      <button onClick={rodarDiagnosticoEspera} disabled={carregandoDiagEspera}
                        style={{ marginTop: 12, padding: '6px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.texto, fontSize: 12, cursor: carregandoDiagEspera ? 'not-allowed' : 'pointer' }}>
                        🩺 {carregandoDiagEspera ? t('configuracao_ia.espera_diag_rodando') : t('configuracao_ia.espera_diag')}
                      </button>

                      {diagEspera && (
                        <div style={{ marginTop: 10, padding: 10, borderRadius: 6, background: C.card, border: `1px solid ${C.border}`, fontSize: 12, color: C.texto }}>
                          {(diagEspera.passos || []).map((p, i) => (
                            <div key={`ep${i}`} style={{ marginBottom: 6 }}>
                              <span>{p.ok ? '✅' : '❌'} <strong>{p.titulo}</strong></span>
                              {p.detalhe && (
                                <div style={{ color: C.textoSecundario, whiteSpace: 'pre-wrap', marginLeft: 20 }}>{p.detalhe}</div>
                              )}
                            </div>
                          ))}
                          {(diagEspera.alertas || []).map((a, i) => (
                            <div key={`ea${i}`} style={{ marginTop: 8, color: C.alerta, fontWeight: 600 }}>⚠️ {a}</div>
                          ))}
                          {(diagEspera.previa || []).length > 0 && (
                            <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('configuracao_ia.espera_previa')}</div>
                              {diagEspera.previa.map((c, i) => (
                                <div key={`ev${i}`} style={{ color: c.sairia ? C.texto : C.textoMudo, marginBottom: 2 }}>
                                  {c.sairia ? '•' : '–'} {c.nome} · {c.espera}
                                  {c.motivo ? ` — ${c.motivo}` : ''}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={salvarAgenda} disabled={salvandoAgenda}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, cursor: salvandoAgenda ? 'not-allowed' : 'pointer', background: C.primaria, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600 }}>
                    <Save size={14} /> {salvandoAgenda ? t('configuracao_ia.btn_salvando') : t('configuracao_ia.btn_salvar_agenda')}
                  </button>
                  <button onClick={testarAgenda} disabled={testandoAgenda}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, cursor: testandoAgenda ? 'not-allowed' : 'pointer', background: C.card, border: `1px solid ${C.border}`, color: C.texto, fontSize: 14, fontWeight: 600 }}>
                    <CheckCircle2 size={14} /> {testandoAgenda ? t('configuracao_ia.agenda_testando') : t('configuracao_ia.agenda_testar')}
                  </button>
                </div>

                {/* Aviso persistente: o toast some, e este é o caso em que o
                    cliente acha que está tudo certo porque a IA agenda. */}
                {avisoApiAgenda && (
                  <div style={{ marginTop: 12, padding: 10, borderRadius: 6, background: '#7f1d1d22', border: `1px solid ${C.alerta}`, fontSize: 12, color: C.texto, lineHeight: 1.5 }}>
                    ⚠️ {avisoApiAgenda}
                  </div>
                )}
              </>
            )}

            {!agenda.provedor && (
              <p style={{ fontSize: 13, color: C.textoMudo, marginTop: 4 }}>
                {t('configuracao_ia.agenda_selecione_provedor')}
              </p>
            )}
          </div>
        );
      })()}

      {config && instancia && (
        <div style={{ ...card, marginTop: 16 }}>
          <button
            onClick={() => setModoAvancado(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%', background: 'none', border: 'none', cursor: 'pointer',
              padding: 0, color: C.texto
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 15 }}>{t('configuracao_ia.avancado_titulo')}</span>
            {modoAvancado ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {modoAvancado && (
            <div style={{ marginTop: 16 }}>
              <label style={labelBase}>{t('configuracao_ia.label_persona')}</label>
              <textarea
                rows={4}
                value={config.persona || ''}
                onChange={e => atualizarConfig('persona', e.target.value)}
                style={{ ...inputBase, marginBottom: 16, resize: 'vertical', fontFamily: 'inherit' }}
              />

              <label style={labelBase}>{t('configuracao_ia.label_conhecimento')}</label>
              <textarea
                rows={8}
                value={config.base_conhecimento || ''}
                onChange={e => atualizarConfig('base_conhecimento', e.target.value)}
                style={{ ...inputBase, marginBottom: 16, resize: 'vertical', fontFamily: 'inherit' }}
              />

              <label style={labelBase}>{t('configuracao_ia.label_funil')}</label>
              <textarea
                rows={6}
                value={config.fluxo_qualificacao || ''}
                onChange={e => atualizarConfig('fluxo_qualificacao', e.target.value)}
                style={{ ...inputBase, marginBottom: 16, resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
              />

              <label style={labelBase}>
                {t('configuracao_ia.label_delay', { s: config.debounce_segundos ?? 20 })}
              </label>
              <input
                type="range" min="0" max="30" step="1"
                value={config.debounce_segundos ?? 20}
                onChange={e => atualizarConfig('debounce_segundos', parseInt(e.target.value, 10))}
                style={{ width: '100%', maxWidth: 360, marginBottom: 6 }}
              />
              <p style={{ fontSize: 12, color: C.textoMudo, marginBottom: 16 }}>
                {t('configuracao_ia.delay_recomendado')}
              </p>

              <label style={labelBase}>{t('configuracao_ia.label_pausa_horas')}</label>
              <input
                type="number" min="1" max="48"
                value={config.pausa_horas ?? 4}
                onChange={e => atualizarConfig('pausa_horas', parseInt(e.target.value, 10) || 4)}
                style={{ ...inputBase, marginBottom: 20, maxWidth: 120 }}
              />

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                <button
                  onClick={() => salvarAvancado()}
                  disabled={salvando}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '10px 18px', borderRadius: 6,
                    cursor: salvando ? 'not-allowed' : 'pointer',
                    background: C.primaria, border: 'none',
                    color: '#fff', fontSize: 14, fontWeight: 600
                  }}
                >
                  <Save size={14} /> {salvando ? t('configuracao_ia.btn_salvando') : t('configuracao_ia.btn_salvar_avancado')}
                </button>
                <button
                  onClick={rodarDiagnostico}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '10px 18px', borderRadius: 6, cursor: 'pointer',
                    background: C.card, border: `1px solid ${C.border}`,
                    color: C.texto, fontSize: 14, fontWeight: 600
                  }}
                >
                  <Stethoscope size={14} /> {t('configuracao_ia.btn_diagnostico')}
                </button>
              </div>

              {avisosConflito.length > 0 && (
                <div style={{
                  background: '#fffbeb', border: '1px solid #f59e0b',
                  borderRadius: 8, padding: 14, marginBottom: 16
                }}>
                  <strong style={{ color: '#92400e', display: 'block', marginBottom: 8, fontSize: 13 }}>
                    ⚠️ {t('configuracao_ia.conflito_titulo')}
                  </strong>
                  <ul style={{ margin: 0, paddingLeft: 18, color: '#78350f', fontSize: 13, lineHeight: 1.6 }}>
                    {avisosConflito.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}

              {diagnostico && (
                <div style={{ background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 16 }}>
                  <h3 style={{ marginTop: 0, marginBottom: 12, color: C.texto, fontSize: 14 }}>{t('configuracao_ia.diag_titulo')}</h3>
                  {diagnostico.problemas.length === 0 ? (
                    <div style={{ color: C.ok, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                      <CheckCircle2 size={16} /> {t('configuracao_ia.diag_ok')}
                    </div>
                  ) : (
                    <div style={{ marginBottom: 12 }}>
                      {diagnostico.problemas.map((p, i) => (
                        <div key={i} style={{ color: C.alerta, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <XCircle size={16} /> {p}
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, fontSize: 13 }}>
                    {Object.entries(diagnostico.checks).map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {typeof v === 'boolean'
                          ? (v ? <CheckCircle2 size={14} color={C.ok} /> : <XCircle size={14} color={C.alerta} />)
                          : <span style={{ width: 14 }} />}
                        <span style={{ color: C.textoSecundario }}>{k.replace(/_/g, ' ')}:</span>
                        <strong style={{ color: C.texto }}>{String(v)}</strong>
                      </div>
                    ))}
                  </div>
                  {diagnostico.mensagens_recentes?.length > 0 && (
                    <details style={{ marginTop: 12 }}>
                      <summary style={{ cursor: 'pointer', color: C.textoSecundario, fontSize: 13 }}>
                        {t('configuracao_ia.diag_ultimas', { n: diagnostico.mensagens_recentes.length })}
                      </summary>
                      <div style={{ marginTop: 8, fontSize: 12, fontFamily: 'monospace', color: C.textoSecundario }}>
                        {diagnostico.mensagens_recentes.map((m, i) => (
                          <div key={i} style={{ marginBottom: 4 }}>
                            [{new Date(m.ts).toLocaleString('pt-BR')}] {m.de_mim ? '→' : '←'}
                            {m.gerada_por_ia ? ' [IA]' : ''} {m.telefone}: {(m.texto || '').substring(0, 80)}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              <div>
                <h3 style={{ marginTop: 0, marginBottom: 6, color: C.texto, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FlaskConical size={16} /> {t('configuracao_ia.teste_titulo')}
                </h3>
                <p style={{ fontSize: 13, color: C.textoSecundario, marginTop: 0, marginBottom: 12 }}>
                  {t('configuracao_ia.teste_desc')}
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={perguntaTeste}
                    onChange={e => setPerguntaTeste(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && testar()}
                    placeholder={t('configuracao_ia.placeholder_teste')}
                    style={{ ...inputBase }}
                  />
                  <button
                    onClick={testar}
                    disabled={!perguntaTeste.trim() || testando}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '8px 16px', borderRadius: 6,
                      cursor: testando ? 'not-allowed' : 'pointer',
                      background: C.primaria, border: 'none',
                      color: '#fff', fontSize: 14, fontWeight: 600, flexShrink: 0
                    }}
                  >
                    {testando ? t('configuracao_ia.btn_testando') : t('configuracao_ia.btn_testar')}
                  </button>
                </div>
                {respostaTeste && (
                  <div style={{
                    marginTop: 12, padding: 12,
                    background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: 6,
                    whiteSpace: 'pre-wrap', fontSize: 14, color: C.texto
                  }}>
                    {respostaTeste}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
